import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, notification } from "antd";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  IoBanOutline,
  IoCheckmarkCircleOutline,
  IoRefreshOutline,
  IoSaveOutline,
} from "react-icons/io5";
import { useAuth } from "../../services/AuthContext";
import { normalizeLanguage } from "../../services/language";
import { useInterfaceLanguage } from "../../hooks/useInterfaceLanguage";
import {
  BLOCK_TYPE_COMMON,
  BLOCK_TYPE_MATERIAL,
  BLOCK_TYPE_ANIMATION,
  BLOCK_TYPE_SPECIAL,
  MATERIAL_TEXTURE_PRODUCTS,
  BLOCK_ANIMATION_PRODUCTS,
  STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER,
  STORE_PRODUCT_ACCESS_PURCHASE,
  resolveSkinStoreProductForSkin,
} from "../../constants/skinStoreCatalog";
import { t as i18nT } from "../../i18n";
import { resolveAvatarSrc } from "../../utils/avatarUrl";
import DesignModePreview from "./DesignModePreview";
import {
  BLOCK_FILTERS,
  BLOCK_STYLES,
  BLOCK_STYLE_STAR_THUMBS,
  BOARD_FILTERS,
  BOARD_PRESETS,
  COLOR_MATERIALS,
  DEFAULT_BOARD_DESIGN,
  STATUS_FILTERS,
  TEAM_COLORS,
  TEXTURE_MATERIALS,
  getBlockStyle,
  getColorMaterial,
  getTextureMaterial,
  getTrimOption,
  normalizeBoardDesign,
  readMyBoards,
  readStoredBoardDesign,
  sameBoardParts,
  studioAsset,
  TRIM_OPTIONS,
  writeMyBoards,
  writeStoredBoardDesign,
} from "./designStudio/catalog";
import { CoverFlow, StudioBadge, StudioChips, StudioIcon, StudioLegend } from "./designStudio/StudioParts";
import "./designStudio/designStudio.css";

const isOrigPart = (value) => !value || value === "orig";
const MATERIAL_TYPE_FILTERS = [
  { id: "all", label: "Todo" },
  { id: "colores", label: "Colores" },
  { id: "piedra", label: "Piedra" },
  { id: "madera", label: "Madera" },
  { id: "naturaleza", label: "Naturaleza" },
  { id: "banderas", label: "Banderas" },
];


const PROFILE_STORAGE_PREFIX = "xima_profile_v1_";
const SPECIAL_BLOCK_STORAGE_PREFIX = "xima_special_block_v1_";
const COMMON_BLOCK_STORAGE_PREFIX = "xima_common_block_v1_";
const GLOBAL_SPECIAL_BLOCK_KEY = "xima_special_block_selected";
const GLOBAL_SPECIAL_BLOCK_IMAGE_KEY = "xima_special_block_image";
const GLOBAL_BOARD_THEME_KEY = "xima_board_theme_selected";
const GLOBAL_COMMON_BLOCK_THEME_KEY = "xima_common_block_theme_selected";
const GLOBAL_BLOCK_MATERIAL_TEXTURE_KEY = "xima_block_material_texture_selected";
const GLOBAL_BOARD_WALL_TEXTURE_KEY = "xima_board_wall_texture_selected";
// Letras y números del borde del tablero: el juego (Unity) lee estas claves.
const BOARD_COORD_FONT_KEY = "xima_board_coord_font";
const BOARD_COORD_SCALE_KEY = "xima_board_coord_scale";
const BOARD_COORD_SCALE_MIN = 0.6;
const BOARD_COORD_SCALE_MAX = 2.2;
const BOARD_COORD_FONT_OPTIONS = [
  { id: "classic", label: "Clásica" },
  { id: "bold", label: "Gruesa" },
  { id: "mono", label: "Máquina" },
  { id: "light", label: "Brillante" },
];
const readBoardCoordSetting = (key, fallback) => {
  try {
    return window.localStorage.getItem(key) || fallback;
  } catch (error) {
    return fallback;
  }
};
const clampBoardCoordScale = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  return Math.min(BOARD_COORD_SCALE_MAX, Math.max(BOARD_COORD_SCALE_MIN, numeric));
};
const GLOBAL_GAME_BACKGROUND_KEY = "xima_game_background_selected";
const GLOBAL_BLOCK_ANIMATION_KEY = "xima_block_animation_selected";
const DEFAULT_SPECIAL_BLOCK_ID = "default_star";
const DEFAULT_COMMON_BLOCK_ID = "default_star";
const DEFAULT_BOARD_THEME_ID = "classic";
const DEFAULT_MATERIAL_TEXTURE_ID = "default";
const DEFAULT_GAME_BACKGROUND_ID = "galaxy-default";
const DEFAULT_BLOCK_ANIMATION_ID = "default";
const SKIN_ASSET_VERSION = "20261005-flags-mexico";
const MAX_AVATAR_FILE_SIZE_BYTES = 3 * 1024 * 1024;
const PROFILE_CONTACT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DESIGN_COPY = Object.freeze({
  designMode: {
    es: "Modo Diseñar",
    en: "Design mode",
    de: "Designmodus",
    ru: "Режим дизайна",
  },
  backToLobby: {
    es: "Volver al lobby",
    en: "Back to lobby",
    de: "Zurück zur Lobby",
    ru: "Назад в лобби",
  },
  closeDesignMode: {
    es: "Cerrar modo diseñar",
    en: "Close design mode",
    de: "Designmodus schließen",
    ru: "Закрыть режим дизайна",
  },
  playerProfile: {
    es: "Perfil del jugador",
    en: "Player profile",
    de: "Spielerprofil",
    ru: "Профиль игрока",
  },
  playerFallback: {
    es: "Jugador",
    en: "Player",
    de: "Spieler",
    ru: "Игрок",
  },
  profileDescription: {
    es: "Edita tu información y selecciona tu foto de perfil.",
    en: "Edit your information and select your profile photo.",
    de: "Bearbeite deine Informationen und wähle dein Profilfoto aus.",
    ru: "Отредактируйте свою информацию и выберите фото профиля.",
  },
  personalInformation: {
    es: "Información personal",
    en: "Personal information",
    de: "Persönliche Informationen",
    ru: "Личная информация",
  },
  firstName: {
    es: "Nombre",
    en: "First name",
    de: "Vorname",
    ru: "Имя",
  },
  lastName: {
    es: "Apellido",
    en: "Last name",
    de: "Nachname",
    ru: "Фамилия",
  },
  emailOptional: {
    es: "Correo (opcional)",
    en: "Email (optional)",
    de: "E-Mail (optional)",
    ru: "Электронная почта (необязательно)",
  },
  phoneOptional: {
    es: "Teléfono (opcional)",
    en: "Phone (optional)",
    de: "Telefon (optional)",
    ru: "Телефон (необязательно)",
  },
  changePasswordOptional: {
    es: "Cambiar contraseña (opcional)",
    en: "Change password (optional)",
    de: "Passwort ändern (optional)",
    ru: "Изменить пароль (необязательно)",
  },
  current: {
    es: "Actual",
    en: "Current",
    de: "Aktuell",
    ru: "Текущий",
  },
  newPasswordLabel: {
    es: "Nueva",
    en: "New",
    de: "Neu",
    ru: "Новый",
  },
  confirm: {
    es: "Confirmar",
    en: "Confirm",
    de: "Bestätigen",
    ru: "Подтвердить",
  },
  currentPassword: {
    es: "Contraseña actual",
    en: "Current password",
    de: "Aktuelles Passwort",
    ru: "Текущий пароль",
  },
  newPassword: {
    es: "Nueva contraseña",
    en: "New password",
    de: "Neues Passwort",
    ru: "Новый пароль",
  },
  repeatPassword: {
    es: "Repite la contraseña",
    en: "Repeat password",
    de: "Passwort wiederholen",
    ru: "Повторите пароль",
  },
  uploadPhoto: {
    es: "Subir foto",
    en: "Upload photo",
    de: "Foto hochladen",
    ru: "Загрузить фото",
  },
  chooseFile: {
    es: "Elegir archivo",
    en: "Choose file",
    de: "Datei auswählen",
    ru: "Выбрать файл",
  },
  recommendedPhotoFormat: {
    es: "Formato recomendado: JPG/PNG, máximo 3 MB.",
    en: "Recommended format: JPG/PNG, maximum 3 MB.",
    de: "Empfohlenes Format: JPG/PNG, maximal 3 MB.",
    ru: "Рекомендуемый формат: JPG/PNG, максимум 3 МБ.",
  },
  presetAvatars: {
    es: "Avatares preestablecidos",
    en: "Preset avatars",
    de: "Voreingestellte Avatare",
    ru: "Готовые аватары",
  },
  boardColor: {
    es: "Color y textura de casillas",
    en: "Square color and texture",
    de: "Farbe und Textur der Felder",
    ru: "Цвет и текстура клеток",
  },
  boardColorDescription: {
    es: "Elige un color o textura para las casillas del tablero de Xima.",
    en: "Choose a color or texture for the Xima board squares.",
    de: "Wähle eine Farbe oder Textur für die Felder des Xima-Spielbretts.",
    ru: "Выберите цвет или текстуру клеток доски Xima.",
  },
  specialBlock: {
    es: "Bloque especial",
    en: "Special block",
    de: "Spezialblock",
    ru: "Специальный блок",
  },
  specialBlockDescription: {
    es: "Elige el bloque especial para tu perfil.",
    en: "Choose the special block for your profile.",
    de: "Wähle den Spezialblock für dein Profil.",
    ru: "Выберите специальный блок для своего профиля.",
  },
  commonBlocks: {
    es: "Bloques comunes",
    en: "Common blocks",
    de: "Normale Blöcke",
    ru: "Обычные блоки",
  },
  commonBlocksDescription: {
    es: "Selecciona la imagen que quieres usar para los bloques comunes.",
    en: "Select the image you want to use for the common blocks.",
    de: "Wähle das Bild aus, das du für die normalen Blöcke verwenden möchtest.",
    ru: "Выберите изображение для обычных блоков.",
  },
  free: {
    es: "Gratis",
    en: "Free",
    de: "Kostenlos",
    ru: "Бесплатно",
  },
  paid: {
    es: "De pago",
    en: "Paid",
    de: "Kostenpflichtig",
    ru: "Платно",
  },
  earned: {
    es: "Ganadas",
    en: "Earned",
    de: "Verdient",
    ru: "Полученные",
  },
  unlocked: {
    es: "Desbloqueado",
    en: "Unlocked",
    de: "Freigeschaltet",
    ru: "Разблокировано",
  },
  earnedRequiresTop1: {
    es: "Ganada: requiere top #1",
    en: "Earned: requires top #1",
    de: "Verdient: erfordert Top #1",
    ru: "Получено: требуется топ #1",
  },
  defaultStar: {
    es: "Default Star",
    en: "Default Star",
    de: "Standardstern",
    ru: "Стандартная звезда",
  },
  earnedTopRequired: {
    es: "\"{label}\" requiere haber sido top en el modo {mode}.",
    en: "\"{label}\" requires being top in {mode} mode.",
    de: "\"{label}\" erfordert Platz 1 im Modus {mode}.",
    ru: "Для \"{label}\" нужно быть лидером в режиме {mode}.",
  },
  skinUnavailable: {
    es: "Este skin no está disponible con tu estado actual.",
    en: "This skin is not available with your current status.",
    de: "Dieser Skin ist mit deinem aktuellen Status nicht verfügbar.",
    ru: "Этот скин недоступен при вашем текущем статусе.",
  },
  buySkinQuestion: {
    es: "\"{label}\" no está desbloqueado. ¿Quieres comprarlo ahora o rechazar?",
    en: "\"{label}\" is not unlocked. Do you want to buy it now or decline?",
    de: "\"{label}\" ist nicht freigeschaltet. Möchtest du ihn jetzt kaufen oder ablehnen?",
    ru: "\"{label}\" не разблокирован. Хотите купить его сейчас или отказаться?",
  },
  buy: {
    es: "Comprar",
    en: "Buy",
    de: "Kaufen",
    ru: "Купить",
  },
  reject: {
    es: "Rechazar",
    en: "Decline",
    de: "Ablehnen",
    ru: "Отказаться",
  },
  scienceModeName: {
    es: "Ciencia",
    en: "Science",
    de: "Wissenschaft",
    ru: "Наука",
  },
  chanceModeName: {
    es: "Azar",
    en: "Chance",
    de: "Zufall",
    ru: "Случайность",
  },
  cienzarModeName: {
    es: "Cienzar",
    en: "Cienzar",
    de: "Cienzar",
    ru: "Cienzar",
  },
  noPhoto: {
    es: "Sin foto",
    en: "No photo",
    de: "Kein Foto",
    ru: "Без фото",
  },
  reset: {
    es: "Restablecer",
    en: "Reset",
    de: "Zurücksetzen",
    ru: "Сбросить",
  },
  cancel: {
    es: "Cancelar",
    en: "Cancel",
    de: "Abbrechen",
    ru: "Отмена",
  },
  saveProfile: {
    es: "Guardar perfil",
    en: "Save profile",
    de: "Profil speichern",
    ru: "Сохранить профиль",
  },
  saving: {
    es: "Guardando...",
    en: "Saving...",
    de: "Wird gespeichert...",
    ru: "Сохранение...",
  },
  flags: {
    es: "Banderas",
    en: "Flags",
    de: "Flaggen",
    ru: "Флаги",
  },
  skinImages: {
    es: "Imágenes",
    en: "Images",
    de: "Bilder",
    ru: "Изображения",
  },
  wallTextures: {
    es: "Texturas",
    en: "Textures",
    de: "Texturen",
    ru: "Текстуры",
  },
  noSkins: {
    es: "Sin skins en esta categoría.",
    en: "No skins in this category.",
    de: "Keine Skins in dieser Kategorie.",
    ru: "В этой категории нет скинов.",
  },
  firstNamePlaceholder: {
    es: "Tu nombre",
    en: "Your first name",
    de: "Dein Vorname",
    ru: "Ваше имя",
  },
  lastNamePlaceholder: {
    es: "Tu apellido",
    en: "Your last name",
    de: "Dein Nachname",
    ru: "Ваша фамилия",
  },
});

const resolvePublicAssetPath = (relativePath) => {
  const prefix = String(process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  const normalized = String(relativePath || "").replace(/^\/+/, "");
  return prefix ? `${prefix}/${normalized}` : `/${normalized}`;
};

const withAssetVersion = (assetPath) => {
  const value = String(assetPath || "").trim();
  if (!value || value.startsWith("data:") || value.includes("?v=")) return value;
  return `${value}${value.includes("?") ? "&" : "?"}v=${SKIN_ASSET_VERSION}`;
};

const resolveVersionedPublicAssetPath = (relativePath) => withAssetVersion(resolvePublicAssetPath(relativePath));

const stripAssetVersion = (value) => String(value || "").split("?")[0];

const AVATAR_PRESETS = [
  { id: "aurora", name: "AURORA", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-aurora.png") },
  { id: "ember", name: "EMBER", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-ember.png") },
  { id: "verdant", name: "VERDANT", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-verdant.png") },
  { id: "royal", name: "ROYAL", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-royal.png") },
  { id: "solar", name: "SOLAR", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-solar.png") },
  { id: "tide", name: "TIDE", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-tide.png") },
];

const WOOD_BOARD_PREVIEW_BACKGROUND =
  "radial-gradient(circle at 32% 24%, rgba(210,146,78,0.16), transparent 34%), linear-gradient(100deg, rgba(46,18,6,0.98), rgba(112,53,19,0.96) 48%, rgba(58,22,7,0.98))";

const BOARD_THEMES = [
  { id: "classic", label: i18nT("profile.classicNeon"), light: "#f2f0e8", dark: "#005c4a", glow: "#00be95" },
  { id: "cosmic", label: i18nT("profile.cosmicBlue"), light: "#f2f0e8", dark: "#082c7a", glow: "#2da0ee" },
  { id: "magma", label: "Magma", light: "#f2f0e8", dark: "#7a260f", glow: "#e65c22" },
  { id: "emerald", label: "Madera", light: "#8a481a", dark: "#3f1708", glow: "#b7662d", previewBackground: WOOD_BOARD_PREVIEW_BACKGROUND },
  { id: "royal", label: "Royal", light: "#f2f0e8", dark: "#3927a3", glow: "#7e66ee" },
  { id: "mono", label: i18nT("profile.monoNight"), light: "#f2f0e8", dark: "#293140", glow: "#8b9ab4" },
];

const MATERIAL_TEXTURE_OPTIONS = [
  {
    id: DEFAULT_MATERIAL_TEXTURE_ID,
    label: "Colores originales",
    image: resolveVersionedPublicAssetPath("special-blocks/star.svg"),
    productId: "",
  },
  ...MATERIAL_TEXTURE_PRODUCTS.map((product) => ({
    id: product.materialTextureId,
    label: product.label,
    image: withAssetVersion(product.image),
    previewTexture: resolveVersionedPublicAssetPath(
      `design-preview/materials/${product.materialTextureId}/color.png`,
    ),
    previewNormal: resolveVersionedPublicAssetPath(
      `design-preview/materials/${product.materialTextureId}/normal.png`,
    ),
    previewRoughness: resolveVersionedPublicAssetPath(
      `design-preview/materials/${product.materialTextureId}/roughness.png`,
    ),
    previewMetalness: resolveVersionedPublicAssetPath(
      `design-preview/materials/${product.materialTextureId}/metalness.png`,
    ),
    productId: product.id,
  })),
];

const BLOCK_ANIMATION_OPTIONS = [
  {
    id: DEFAULT_BLOCK_ANIMATION_ID,
    label: "Cubo original",
    image: resolveVersionedPublicAssetPath("special-blocks/star.svg"),
    productId: "",
  },
  ...BLOCK_ANIMATION_PRODUCTS.map((product) => ({
    id: product.animationId,
    label: product.label,
    image: withAssetVersion(product.image),
    model: resolveVersionedPublicAssetPath(
      `design-preview/block-models/${product.animationId}.glb`,
    ),
    productId: product.id,
  })),
];

const GAME_BACKGROUND_OPTIONS = [
  {
    id: DEFAULT_GAME_BACKGROUND_ID,
    label: "Galaxia XIMA",
    image: "",
    previewClassName: "is-galaxy-default",
  },
  { id: "aurora-realm", label: "Reino Aurora", image: resolveVersionedPublicAssetPath("game-backgrounds/aurora-realm.png") },
  { id: "volcanic-realm", label: "Reino Volcánico", image: resolveVersionedPublicAssetPath("game-backgrounds/volcanic-realm.png") },
  { id: "bioluminescent-forest", label: "Bosque Bioluminiscente", image: resolveVersionedPublicAssetPath("game-backgrounds/bioluminescent-forest.png") },
  { id: "abyssal-ocean", label: "Océano Abisal", image: resolveVersionedPublicAssetPath("game-backgrounds/abyssal-ocean.png") },
  { id: "purple-nebula", label: "Nebulosa Púrpura", image: resolveVersionedPublicAssetPath("game-backgrounds/purple-nebula.png") },
  { id: "night-desert", label: "Desierto Nocturno", image: resolveVersionedPublicAssetPath("game-backgrounds/night-desert.png") },
  {
    id: "none",
    label: "Sin fondo",
    image: "",
    plainColor: "#0a0d14",
    previewClassName: "is-no-background",
  },
];

const makePngSkinItem = (folder, id, status, label = id) => ({
  id,
  label,
  asset: resolveVersionedPublicAssetPath(`skins/${folder}/${encodeURIComponent(`${id}.png`)}`),
  status,
});

// One entry per flag: a square skin for common blocks, a circular skin for
// special blocks (so both kinds never look alike) and a 3:2 board wall image.
const FLAG_CATALOG = [
  { key: "mexico", commonId: "BanderaMexico", specialId: "BanderaMexicoCircular", names: { es: "México", en: "Mexico", de: "Mexiko", ru: "Мексика" } },
  { key: "argentina", commonId: "BanderaArgentina", specialId: "BanderaArgentinaCircular", names: { es: "Argentina", en: "Argentina", de: "Argentinien", ru: "Аргентина" } },
  { key: "chile", commonId: "BanderaChile", specialId: "BanderaChileCircular", names: { es: "Chile", en: "Chile", de: "Chile", ru: "Чили" } },
  { key: "el-salvador", commonId: "BanderaElSalvador", specialId: "BanderaElSalvadorCircular", names: { es: "El Salvador", en: "El Salvador", de: "El Salvador", ru: "Сальвадор" } },
  { key: "canada", commonId: "BanderaCanada", specialId: "BanderaCanadaCircular", names: { es: "Canadá", en: "Canada", de: "Kanada", ru: "Канада" } },
  { key: "usa", commonId: "BanderaEstadosUnidosEspecial", specialId: "BanderaEstadosUnidosCircular", names: { es: "Estados Unidos", en: "United States", de: "Vereinigte Staaten", ru: "США" } },
  { key: "cuba", commonId: "BanderaCubanaEspecial", specialId: "BanderaCubaCircular", names: { es: "Cuba", en: "Cuba", de: "Kuba", ru: "Куба" } },
  { key: "brazil", commonId: "BanderaBrasil", specialId: "BanderaBrasilCircular", names: { es: "Brasil", en: "Brazil", de: "Brasilien", ru: "Бразилия" } },
  { key: "puerto-rico", commonId: "BanderaPuertoRico", specialId: "BanderaPuertoRicoCircular", names: { es: "Puerto Rico", en: "Puerto Rico", de: "Puerto Rico", ru: "Пуэрто-Рико" } },
  { key: "colombia", commonId: "BanderaColombia", specialId: "BanderaColombiaCircular", names: { es: "Colombia", en: "Colombia", de: "Kolumbien", ru: "Колумбия" } },
  { key: "dominican-republic", commonId: "BanderaRepublicaDominicana", specialId: "BanderaRepublicaDominicanaCircular", names: { es: "República Dominicana", en: "Dominican Republic", de: "Dominikanische Republik", ru: "Доминиканская Республика" } },
  { key: "peru", commonId: "BanderaPeru", specialId: "BanderaPeruCircular", names: { es: "Perú", en: "Peru", de: "Peru", ru: "Перу" } },
  { key: "venezuela", commonId: "BanderaVenezuela", specialId: "BanderaVenezuelaCircular", names: { es: "Venezuela", en: "Venezuela", de: "Venezuela", ru: "Венесуэла" } },
  { key: "uruguay", commonId: "BanderaUruguay", specialId: "BanderaUruguayCircular", names: { es: "Uruguay", en: "Uruguay", de: "Uruguay", ru: "Уругвай" } },
  { key: "panama", commonId: "BanderaPanama", specialId: "BanderaPanamaCircular", names: { es: "Panamá", en: "Panama", de: "Panama", ru: "Панама" } },
  { key: "costa-rica", commonId: "BanderaCostaRica", specialId: "BanderaCostaRicaCircular", names: { es: "Costa Rica", en: "Costa Rica", de: "Costa Rica", ru: "Коста-Рика" } },
  { key: "japan", commonId: "BanderaJapon", specialId: "BanderaJaponCircular", names: { es: "Japón", en: "Japan", de: "Japan", ru: "Япония" } },
  { key: "china", commonId: "BanderaChina", specialId: "BanderaChinaCircular", names: { es: "China", en: "China", de: "China", ru: "Китай" } },
  { key: "spain", commonId: "BanderaEspañolaEspecial", specialId: "BanderaEspanaCircular", names: { es: "España", en: "Spain", de: "Spanien", ru: "Испания" } },
  { key: "germany", commonId: "BanderaAlemanaEspoecial", specialId: "BanderaAlemaniaCircular", names: { es: "Alemania", en: "Germany", de: "Deutschland", ru: "Германия" } },
  { key: "ukraine", commonId: "Bandera UcraneanaEspecial", specialId: "BanderaUcraniaCircular", names: { es: "Ucrania", en: "Ukraine", de: "Ukraine", ru: "Украина" } },
];

// Square flags that used to be special skins; special blocks now use the
// circular version of the same flag.
const LEGACY_SPECIAL_FLAG_SKIN_MAP = Object.freeze(
  FLAG_CATALOG.filter((flag) => /Especial$/.test(flag.commonId)).reduce(
    (map, flag) => ({ ...map, [flag.commonId]: flag.specialId }),
    {},
  ),
);

const makeFlagSkinItem = (folder, id, flag) => ({
  ...makePngSkinItem(folder, id, "Gratis", flag.names.es),
  labels: flag.names,
});

const COMMON_FLAG_SKINS = FLAG_CATALOG.map((flag) => makeFlagSkinItem("common", flag.commonId, flag));
const SPECIAL_FLAG_SKINS = FLAG_CATALOG.map((flag) => makeFlagSkinItem("special", flag.specialId, flag));

const WALL_FLAG_OPTIONS = FLAG_CATALOG.map((flag) => {
  const image = resolveVersionedPublicAssetPath(`flags/walls/flag-${flag.key}.png`);
  return {
    id: `flag-${flag.key}`,
    label: flag.names.es,
    labels: flag.names,
    image,
    previewTexture: image,
    productId: "",
    isFlag: true,
  };
});

// Colores lisos para las paredes (gratis). Mismos tonos que los colores de bloque.
const buildSolidColorImage = (hex) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect width="64" height="64" fill="${hex}"/></svg>`
  )}`;
const WALL_COLOR_OPTIONS = [
  { id: "wall-color-blue", label: "Azul", hex: "#005bff" },
  { id: "wall-color-red", label: "Rojo", hex: "#ff003d" },
  { id: "wall-color-yellow", label: "Amarillo", hex: "#ffea00" },
  { id: "wall-color-green", label: "Verde", hex: "#04ff17" },
  { id: "wall-color-white", label: "Blanco", hex: "#f0f0f0" },
  { id: "wall-color-gray", label: "Gris", hex: "#80858c" },
].map((entry) => {
  const image = buildSolidColorImage(entry.hex);
  return { ...entry, image, previewTexture: image, productId: "", isColor: true };
});

const BOARD_WALL_OPTIONS = [...MATERIAL_TEXTURE_OPTIONS, ...WALL_FLAG_OPTIONS, ...WALL_COLOR_OPTIONS];

const withSkinCounts = (groups) =>
  groups.map((group) => ({
    ...group,
    count: Array.isArray(group.items) ? group.items.length : 0,
  }));

const SPECIAL_FREE_SKINS = [
  { id: "default_star", label: i18nT("profile.defaultStar"), asset: resolveVersionedPublicAssetPath("special-blocks/star.svg"), status: "Gratis" },
  makePngSkinItem("special", "image1-391", "Gratis", "IMAGE1-391"),
  makePngSkinItem("special", "image1-44", "Gratis", "IMAGE1-44"),
  makePngSkinItem("special", "image1-53", "Gratis", "IMAGE1-53"),
  makePngSkinItem("special", "image1-7", "Gratis", "IMAGE1-7"),
  makePngSkinItem("special", "image1-91", "Gratis", "IMAGE1-91"),
  makePngSkinItem("special", "image1-22", "Gratis", "IMAGE1-22"),
  makePngSkinItem("special", "ImagenEstrellaEspecial", "Gratis", "Estrella Especial"),
  makePngSkinItem("special", "Rana", "Gratis", "Rana"),
  makePngSkinItem("special", "Xima9", "Gratis", "Xima9"),
];

const SPECIAL_PAID_SKINS = [
  "thebest1",
  "thebest2",
  "thebest3",
  "thebest4",
  "dama1",
  "dama2",
  "dama3",
  "dama4",
  "estrella1",
  "estrella2",
  "estrella3",
  "estrella4",
  "pelota1",
  "pelota2",
  "pelota3",
  "pelota4",
].map((id) => makePngSkinItem("special", id, "Desbloqueado", id.toUpperCase()));

const SPECIAL_EARNED_SKINS = [
  makePngSkinItem("special", "x1", "Ganada: requiere top #1 ciencia", "X1"),
  makePngSkinItem("special", "x2", "Ganada: requiere top #1 contrazar", "X2"),
  makePngSkinItem("special", "x3", "Ganada: requiere top #1 azar", "X3"),
  makePngSkinItem("special", "x4", "Ganada: requiere top #1 cienzar", "X4"),
];

const SPECIAL_SKIN_GROUPS = [
  {
    key: "free",
    label: "Gratis",
    items: SPECIAL_FREE_SKINS,
  },
  {
    key: "paid",
    label: i18nT("profile.dePago"),
    items: SPECIAL_PAID_SKINS,
  },
  {
    key: "earned",
    label: "Ganadas",
    items: SPECIAL_EARNED_SKINS,
  },
  {
    key: "flags",
    label: "Banderas",
    items: SPECIAL_FLAG_SKINS,
  },
];

const COMMON_FREE_SKINS = [
  { id: "default_star", label: i18nT("profile.defaultStar"), asset: resolveVersionedPublicAssetPath("special-blocks/star.svg"), status: "Gratis" },
  "222222",
  "image1-12",
  "image1-333",
  "image1-39",
  "image1-8",
  "image1-80",
  "image1-83",
  "image1-9",
  "image1-99",
  "Skins2",
].map((entry) =>
  typeof entry === "string"
    ? makePngSkinItem("common", entry, "Gratis", entry.toUpperCase())
    : entry
);

const COMMON_PAID_SKINS = [
  "image1-0",
  "image1-0d",
  "image1-77",
  "image1-8d",
  "image1-9s",
  "image1-17",
  "image1-01",
  "image1-73",
  "image1-76",
  "image1-96",
  "image1-61",
  "image1-27sdfsd",
  "image1-34fcsd",
  "image1-3drfgdfg",
  "image1-6drfgdfgd",
  "image1-65",
  "image1-1sdaf",
  "image1-2dsfgdf",
  "image1-7ds",
  "image1sdfgsd",
].map((id) => makePngSkinItem("common", id, "Desbloqueado", id.toUpperCase()));

const COMMON_SKIN_GROUPS = [
  {
    key: "free",
    label: "Gratis",
    items: COMMON_FREE_SKINS,
  },
  {
    key: "paid",
    label: i18nT("profile.dePago"),
    items: COMMON_PAID_SKINS,
  },
  {
    key: "earned",
    label: "Ganadas",
    items: [],
  },
  {
    key: "flags",
    label: "Banderas",
    items: COMMON_FLAG_SKINS,
  },
];

const SPECIAL_SKIN_GROUPS_WITH_COUNTS = withSkinCounts(SPECIAL_SKIN_GROUPS);
const COMMON_SKIN_GROUPS_WITH_COUNTS = withSkinCounts(COMMON_SKIN_GROUPS);
const COMMON_SKIN_GROUPS_WITH_COUNTS_VISIBLE = COMMON_SKIN_GROUPS_WITH_COUNTS.filter((group) => group.key !== "earned");

const getUserStorageKey = (profile, user) => {
  const candidates = [
    profile?.email,
    profile?.loginIdentifier,
    profile?.username,
    profile?.userName,
    user?.email,
    user?.loginIdentifier,
    user?.username,
    user?.userName,
    user?.id,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  return candidates[0] || "guest";
};

const normalizeSkinId = (value, fallback) => {
  const normalized = String(value || "")
    .trim()
    .replace(/^skins\//i, "")
    .replace(/\.[a-z0-9]+$/i, "");
  if (!normalized || normalized.includes("..") || normalized.includes("/") || normalized.includes("\\")) {
    return fallback;
  }
  return normalized;
};

const getBoardTheme = (id) =>
  BOARD_THEMES.find((theme) => theme.id === id) ||
  BOARD_THEMES.find((theme) => theme.id === DEFAULT_BOARD_THEME_ID) ||
  BOARD_THEMES[0];

const normalizeTopWinnerModeForSkinUnlock = (value) =>
  String(value || "").trim().toLowerCase();

const getSkinAsset = (groups, skinId, fallbackAsset) => {
  const normalized = normalizeSkinId(skinId, "");
  for (const group of groups) {
    const item = group.items.find((entry) => entry.id === normalized);
    if (item?.asset) return item.asset;
  }
  return fallbackAsset;
};

const getProfileAvatar = (profile) =>
  resolveAvatarSrc(
    profile?.currentAvatarUrl ||
      profile?.profileAvatar ||
      profile?.avatarUrl ||
      profile?.avatar ||
      profile?.photoUrl ||
      profile?.pictureUrl ||
      ""
  );

// Colores de bloque para Bot 1..4 (mismos tonos que los jugadores en el juego, más blanco y gris).
const BOT_BLOCK_COLOR_OPTIONS = [
  { id: "blue", label: "Azul", hex: "#005bff" },
  { id: "red", label: "Rojo", hex: "#ff003d" },
  { id: "yellow", label: "Amarillo", hex: "#ffea00" },
  { id: "green", label: "Verde", hex: "#04ff17" },
  { id: "white", label: "Blanco", hex: "#ffffff" },
  { id: "gray", label: "Gris", hex: "#9ea3ab" },
];

const readStoredDesign = (profile, user) => {
  if (typeof window === "undefined") {
    return {
      boardThemeId: DEFAULT_BOARD_THEME_ID,
      specialBlockId: DEFAULT_SPECIAL_BLOCK_ID,
      commonBlockId: DEFAULT_COMMON_BLOCK_ID,
      blockMaterialTextureId: DEFAULT_MATERIAL_TEXTURE_ID,
      boardWallTextureId: DEFAULT_MATERIAL_TEXTURE_ID,
      gameBackgroundId: DEFAULT_GAME_BACKGROUND_ID,
      blockAnimationId: DEFAULT_BLOCK_ANIMATION_ID,
    };
  }

  const userKey = getUserStorageKey(profile, user);
  const boardThemeId = getBoardTheme(
    String(window.localStorage.getItem(GLOBAL_BOARD_THEME_KEY) || "").trim().toLowerCase()
  ).id;
  const storedSpecialBlockId = normalizeSkinId(
    window.localStorage.getItem(`${SPECIAL_BLOCK_STORAGE_PREFIX}${userKey}`) ||
      window.localStorage.getItem(GLOBAL_SPECIAL_BLOCK_KEY) ||
      profile?.specialBlockId,
    DEFAULT_SPECIAL_BLOCK_ID
  );
  const specialBlockId = LEGACY_SPECIAL_FLAG_SKIN_MAP[storedSpecialBlockId] || storedSpecialBlockId;
  const commonBlockId = normalizeSkinId(
    window.localStorage.getItem(`${COMMON_BLOCK_STORAGE_PREFIX}${userKey}`) ||
      window.localStorage.getItem(GLOBAL_COMMON_BLOCK_THEME_KEY) ||
      profile?.commonBlockThemeId,
    DEFAULT_COMMON_BLOCK_ID
  );

  const normalizeMaterialOptionId = (value) => {
    const normalized = normalizeSkinId(value, DEFAULT_MATERIAL_TEXTURE_ID).toLowerCase();
    return MATERIAL_TEXTURE_OPTIONS.some((entry) => entry.id === normalized)
      ? normalized
      : DEFAULT_MATERIAL_TEXTURE_ID;
  };
  const normalizeBackgroundOptionId = (value) => {
    const normalized = normalizeSkinId(value, DEFAULT_GAME_BACKGROUND_ID).toLowerCase();
    return GAME_BACKGROUND_OPTIONS.some((entry) => entry.id === normalized)
      ? normalized
      : DEFAULT_GAME_BACKGROUND_ID;
  };
  const normalizeAnimationOptionId = (value) => {
    const normalized = normalizeSkinId(value, DEFAULT_BLOCK_ANIMATION_ID).toLowerCase();
    return BLOCK_ANIMATION_OPTIONS.some((entry) => entry.id === normalized)
      ? normalized
      : DEFAULT_BLOCK_ANIMATION_ID;
  };
  const blockMaterialTextureId = normalizeMaterialOptionId(
    profile?.blockMaterialTextureId ||
      window.localStorage.getItem(GLOBAL_BLOCK_MATERIAL_TEXTURE_KEY)
  );
  const storedBoardWallTextureId = normalizeSkinId(
    profile?.boardWallTextureId ||
      window.localStorage.getItem(GLOBAL_BOARD_WALL_TEXTURE_KEY),
    DEFAULT_MATERIAL_TEXTURE_ID
  ).toLowerCase();
  const boardWallTextureId = BOARD_WALL_OPTIONS.some((entry) => entry.id === storedBoardWallTextureId)
    ? storedBoardWallTextureId
    : DEFAULT_MATERIAL_TEXTURE_ID;
  const gameBackgroundId = normalizeBackgroundOptionId(
    profile?.gameBackgroundId ||
      window.localStorage.getItem(GLOBAL_GAME_BACKGROUND_KEY)
  );
  const blockAnimationId = normalizeAnimationOptionId(
    profile?.blockAnimationId || window.localStorage.getItem(GLOBAL_BLOCK_ANIMATION_KEY)
  );

  return {
    boardThemeId,
    specialBlockId,
    commonBlockId,
    blockMaterialTextureId,
    boardWallTextureId,
    gameBackgroundId,
    blockAnimationId,
  };
};

const buildBlockPreviewDataUrl = (skinId, asset) => {
  const safeLabel = encodeURIComponent(String(skinId || DEFAULT_SPECIAL_BLOCK_ID));
  const safeAsset = String(asset || "").trim();
  if (safeAsset) return safeAsset;
  return resolvePublicAssetPath(`skins/${safeLabel}.png`);
};

const getDesignCopy = (language, key, values = {}) => {
  const normalizedLanguage = normalizeLanguage(language);
  const entry = DESIGN_COPY[key] || {};
  let copy = entry[normalizedLanguage] || entry.es || entry.en || "";

  Object.entries(values || {}).forEach(([name, value]) => {
    copy = copy.split(`{${name}}`).join(String(value));
  });

  return copy;
};

const getSkinItemLabel = (language, item) => {
  const id = String(item?.id || "").trim().toLowerCase();
  const label = String(item?.label || "").trim();
  if (id === DEFAULT_SPECIAL_BLOCK_ID || label.toLowerCase() === "default star") {
    return getDesignCopy(language, "defaultStar");
  }
  if (item?.labels) return item.labels[normalizeLanguage(language)] || item.labels.es || label;
  return label;
};

const getDesignModeName = (language, mode) => {
  const normalizedMode = String(mode || "").trim().toLowerCase();
  if (normalizedMode === "ciencia" || normalizedMode === "science" || normalizedMode === "free") {
    return getDesignCopy(language, "scienceModeName");
  }
  if (normalizedMode === "azar" || normalizedMode === "chance" || normalizedMode === "dice") {
    return getDesignCopy(language, "chanceModeName");
  }
  if (normalizedMode === "cienzar") {
    return getDesignCopy(language, "cienzarModeName");
  }
  if (normalizedMode === "sciencevsazar" || normalizedMode === "contrazar") {
    return "CONTRAZAR";
  }
  return String(mode || "").trim().toUpperCase();
};

const formatProfileName = (profile, user, fallback = "Jugador") =>
  `${String(profile?.firstName || "").trim()} ${String(profile?.lastName || "").trim()}`.trim() ||
  String(profile?.username || profile?.userName || profile?.loginIdentifier || user?.username || fallback).trim();

const resolveTopWinnerModeUnlocked = (profile, requiredMode) => {
  const entitlements = profile?.skinEntitlements || {};
  const targetMode = normalizeTopWinnerModeForSkinUnlock(requiredMode);
  const modes = Array.isArray(entitlements?.modes) ? entitlements.modes : [];
  if (!targetMode) {
    return Boolean(entitlements?.isTopWinner);
  }
  return modes.some((mode) => normalizeTopWinnerModeForSkinUnlock(mode) === targetMode);
};

const resolveSkinOwnership = (profile, blockType, skinId) => {
  const normalizedSkinId = normalizeSkinId(skinId, "");
  const product = resolveSkinStoreProductForSkin(blockType, normalizedSkinId);
  if (!product?.id) {
    return {
      unlocked: true,
      product: null,
      productId: "",
      accessType: "",
      requiredMode: "",
    };
  }

  const accessType = String(product.accessType || "").trim().toLowerCase() || STORE_PRODUCT_ACCESS_PURCHASE;
  const productId = String(product.id || "").trim();
  if (accessType === STORE_PRODUCT_ACCESS_PURCHASE) {
    const ownedProductIds =
      blockType === BLOCK_TYPE_ANIMATION
        ? Array.isArray(profile?.ownedBlockAnimationProductIds)
          ? profile.ownedBlockAnimationProductIds
          : []
        : blockType === BLOCK_TYPE_MATERIAL
        ? Array.isArray(profile?.ownedMaterialTextureProductIds)
          ? profile.ownedMaterialTextureProductIds
          : []
        : blockType === BLOCK_TYPE_COMMON
        ? Array.isArray(profile?.ownedCommonSkinProductIds)
          ? profile.ownedCommonSkinProductIds
          : []
        : Array.isArray(profile?.ownedSpecialSkinProductIds)
          ? profile.ownedSpecialSkinProductIds
          : [];

    return {
      unlocked: productId ? ownedProductIds.includes(productId) : false,
      product,
      productId,
      accessType,
      requiredMode: "",
    };
  }

  if (accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER) {
    const requiredMode = String(product.earnedMode || "").trim();
    return {
      unlocked: resolveTopWinnerModeUnlocked(profile, requiredMode),
      product,
      productId,
      accessType,
      requiredMode,
    };
  }

  return {
    unlocked: true,
    product,
    productId,
    accessType,
    requiredMode: String(product.earnedMode || "").trim(),
  };
};

const DesignMode = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const language = useInterfaceLanguage(outletContext.language);
  const designText = useCallback(
    (key, values) => getDesignCopy(language, key, values),
    [language],
  );
  const { user, refreshUserProfile } = useAuth();
  const fileInputRef = useRef(null);
  const profileSnapshotRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [avatarId, setAvatarId] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [boardThemeId, setBoardThemeId] = useState(DEFAULT_BOARD_THEME_ID);
  const [specialBlockId, setSpecialBlockId] = useState(DEFAULT_SPECIAL_BLOCK_ID);
  const [commonBlockId, setCommonBlockId] = useState(DEFAULT_COMMON_BLOCK_ID);
  const [blockMaterialTextureId, setBlockMaterialTextureId] = useState(
    DEFAULT_MATERIAL_TEXTURE_ID
  );
  const [boardWallTextureId, setBoardWallTextureId] = useState(
    DEFAULT_MATERIAL_TEXTURE_ID
  );
  const [gameBackgroundId, setGameBackgroundId] = useState(
    DEFAULT_GAME_BACKGROUND_ID
  );
  const [blockAnimationId, setBlockAnimationId] = useState(
    DEFAULT_BLOCK_ANIMATION_ID
  );
  const [designWorkspace, setDesignWorkspace] = useState("board");
  // ---------------------------------------------------------------------
  // Modo Diseñar v2 (pestañas Tablero / Bloques / Fondo / Perfil)
  // ---------------------------------------------------------------------
  const [boardDesign, setBoardDesign] = useState(() => readStoredBoardDesign() || DEFAULT_BOARD_DESIGN);
  const [activePart, setActivePart] = useState("wall");
  const [materialTypeFilter, setMaterialTypeFilter] = useState("all");
  const [materialStatusFilter, setMaterialStatusFilter] = useState("all");
  const [boardFilter, setBoardFilter] = useState("all");
  const [boardCarouselIndex, setBoardCarouselIndex] = useState(0);
  const [myBoards, setMyBoards] = useState(() => readMyBoards());
  const [blockEditing, setBlockEditing] = useState("common");
  const [blockFilter, setBlockFilter] = useState("all");
  const [blockStatusFilter, setBlockStatusFilter] = useState("all");
  const [blockCarouselIndex, setBlockCarouselIndex] = useState(0);
  const [teamColorId, setTeamColorId] = useState("red");
  const [previewAutoRotate, setPreviewAutoRotate] = useState(false);
  const [previewResetSignal, setPreviewResetSignal] = useState(0);
  const [closeLookItem, setCloseLookItem] = useState(null);
  const boardStageRef = useRef(null);
  const boardCarouselSyncedRef = useRef(false);
  const blockCarouselSyncKeyRef = useRef("");
  // Solo admins: Bot 1..4 seleccionado (0 = mi propio diseño) y los diseños guardados.
  const isAdminViewer =
    outletContext.hasAdminMenuAccess === true ||
    [user?.role, user?.type, user?.permission, user?.accountType, user?.accessLevel]
      .map((value) => String(value || "").trim().toLowerCase())
      .some((value) => value === "admin" || value === "superuser");
  const [botDesignSlot, setBotDesignSlot] = useState(0);
  const [botDesigns, setBotDesigns] = useState({});
  const [botColorSavingSlot, setBotColorSavingSlot] = useState(0);

  const resolveOwnership = (blockType, skinId) =>
    botDesignSlot ? { unlocked: true } : resolveSkinOwnership(profile, blockType, skinId);

  const boardTheme = useMemo(() => getBoardTheme(boardThemeId), [boardThemeId]);

  const displayName = useMemo(() => formatProfileName({ ...profile, firstName, lastName }, user, designText("playerFallback")), [
    designText,
    firstName,
    lastName,
    profile,
    user,
  ]);
  const specialAsset = useMemo(
    () => getSkinAsset(SPECIAL_SKIN_GROUPS_WITH_COUNTS, specialBlockId, resolveVersionedPublicAssetPath("special-blocks/star.svg")),
    [specialBlockId]
  );
  const commonAsset = useMemo(
    () => getSkinAsset(COMMON_SKIN_GROUPS_WITH_COUNTS, commonBlockId, resolveVersionedPublicAssetPath("special-blocks/star.svg")),
    [commonBlockId]
  );
  const selectedWallTexture = useMemo(
    () => BOARD_WALL_OPTIONS.find((option) => option.id === boardWallTextureId) || BOARD_WALL_OPTIONS[0],
    [boardWallTextureId],
  );
  const selectedBlockMaterial = useMemo(
    () => MATERIAL_TEXTURE_OPTIONS.find((option) => option.id === blockMaterialTextureId) || MATERIAL_TEXTURE_OPTIONS[0],
    [blockMaterialTextureId],
  );
  const selectedBlockAnimation = useMemo(
    () => BLOCK_ANIMATION_OPTIONS.find((option) => option.id === blockAnimationId) || BLOCK_ANIMATION_OPTIONS[0],
    [blockAnimationId],
  );
  const selectedGameBackground = useMemo(
    () => GAME_BACKGROUND_OPTIONS.find((option) => option.id === gameBackgroundId) || GAME_BACKGROUND_OPTIONS[0],
    [gameBackgroundId],
  );

  const hydrateFromProfile = useCallback(
    (nextProfile) => {
      const resolvedProfile = nextProfile || {};
      const storedDesign = readStoredDesign(resolvedProfile, user);
      const profileAvatar = getProfileAvatar(resolvedProfile);
      const preset = AVATAR_PRESETS.find(
        (entry) => stripAssetVersion(entry.src) === stripAssetVersion(profileAvatar)
      );

      setProfile(resolvedProfile);
      setFirstName(String(resolvedProfile?.firstName || "").trim());
      setLastName(String(resolvedProfile?.lastName || "").trim());
      setContactEmail(String(resolvedProfile?.contactEmail || resolvedProfile?.email || "").trim().toLowerCase());
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setAvatarId(preset?.id || "");
      setAvatarUrl(profileAvatar || AVATAR_PRESETS[1].src);
      setAvatarFile(null);
      setBoardThemeId(storedDesign.boardThemeId);
      setSpecialBlockId(storedDesign.specialBlockId);
      setCommonBlockId(storedDesign.commonBlockId);
      setBlockMaterialTextureId(storedDesign.blockMaterialTextureId);
      setBoardWallTextureId(storedDesign.boardWallTextureId);
      setGameBackgroundId(storedDesign.gameBackgroundId);
      setBlockAnimationId(storedDesign.blockAnimationId);
      setBotDesignSlot(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [user]
  );

  // Recargar solo al cambiar de cuenta: refreshUserProfile tras cada guardado automático
  // no debe pisar lo que el jugador sigue eligiendo.
  const hydrateFromProfileRef = useRef(hydrateFromProfile);
  hydrateFromProfileRef.current = hydrateFromProfile;
  const userRef = useRef(user);
  userRef.current = user;
  const userIdentity = String(user?._id || user?.id || user?.email || "");

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      try {
        const response = await axios.get("/auth/profile");
        if (cancelled) return;
        const nextProfile = response?.data || {};
        profileSnapshotRef.current = nextProfile;
        hydrateFromProfileRef.current(nextProfile);
      } catch (error) {
        if (cancelled) return;
        const fallbackProfile = userRef.current || {};
        profileSnapshotRef.current = fallbackProfile;
        hydrateFromProfileRef.current(fallbackProfile);
        notification.warning({
          message: "Perfil",
          description: i18nT("profile.noSePudoCargarElPerfilCompletoUsando"),
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [userIdentity]);

  useEffect(() => {
    if (!isAdminViewer) return undefined;
    let cancelled = false;
    axios
      .get("/auth/bot-designs")
      .then((response) => {
        if (cancelled) return;
        const nextDesigns = {};
        (response?.data?.designs || []).forEach((entry) => {
          const slot = Number(entry?.slot) || 0;
          if (slot) nextDesigns[slot] = entry;
        });
        setBotDesigns(nextDesigns);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAdminViewer]);

  const applyBlockDesign = (design) => {
    const source = design || {};
    const specialId = normalizeSkinId(source.specialBlockId, DEFAULT_SPECIAL_BLOCK_ID);
    setSpecialBlockId(LEGACY_SPECIAL_FLAG_SKIN_MAP[specialId] || specialId);
    setCommonBlockId(normalizeSkinId(source.commonBlockThemeId || source.commonBlockId, DEFAULT_COMMON_BLOCK_ID));
    setBlockMaterialTextureId(
      normalizeSkinId(source.blockMaterialTextureId, DEFAULT_MATERIAL_TEXTURE_ID).toLowerCase()
    );
    setBlockAnimationId(
      normalizeSkinId(source.blockAnimationId, DEFAULT_BLOCK_ANIMATION_ID).toLowerCase()
    );
  };

  // Pulsar un bot carga su diseño de bloques; pulsarlo otra vez vuelve al diseño propio.
  const selectBotDesignSlot = (slot) => {
    const nextSlot = botDesignSlot === slot ? 0 : slot;
    setBotDesignSlot(nextSlot);
    if (nextSlot) {
      applyBlockDesign(botDesigns[nextSlot]);
    } else {
      // Volver al diseño propio completo: los bots no guardan tablero, así que lo tocado
      // con un bot seleccionado se descarta en vez de pasar a la cuenta.
      const storedDesign = readStoredDesign(profileSnapshotRef.current || profile || {}, user);
      applyBlockDesign(storedDesign);
      setBoardThemeId(storedDesign.boardThemeId);
      setBoardWallTextureId(storedDesign.boardWallTextureId);
      setGameBackgroundId(storedDesign.gameBackgroundId);
    }
  };

  // Cuadraditos de color: marcar uno lo guarda al momento; marcarlo otra vez lo quita
  // y el bot vuelve al color de su posición (azul, rojo, amarillo o verde).
  const toggleBotBlockColor = async (slot, colorId) => {
    const currentColorId = String(botDesigns[slot]?.blockColorId || "");
    const nextColorId = currentColorId === colorId ? "" : colorId;
    try {
      setBotColorSavingSlot(slot);
      const response = await axios.put(`/auth/bot-designs/${slot}`, { blockColorId: nextColorId });
      const saved = response?.data?.design;
      setBotDesigns((current) => ({
        ...current,
        [slot]: saved || { ...(current[slot] || { slot }), blockColorId: nextColorId },
      }));
      const option = BOT_BLOCK_COLOR_OPTIONS.find((entry) => entry.id === nextColorId);
      notification.success({
        message: `Color del Bot ${slot}`,
        description: option
          ? `Los bloques del Bot ${slot} serán de color ${option.label.toLowerCase()}.`
          : `El Bot ${slot} vuelve al color de su posición en la partida.`,
      });
    } catch (error) {
      notification.error({
        message: "No se pudo guardar el color",
        description: error?.response?.data?.message || error?.message || "Intenta de nuevo.",
      });
    } finally {
      setBotColorSavingSlot(0);
    }
  };

  const saveBotDesignConfiguration = async (snapshot) => {
    const { slot, blocks: payload } = snapshot;
    try {
      const response = await axios.put(`/auth/bot-designs/${slot}`, payload);
      const saved = response?.data?.design || { slot, ...payload };
      setBotDesigns((current) => ({ ...current, [slot]: { ...(current[slot] || {}), ...saved } }));
      return true;
    } catch (error) {
      notification.error({
        message: `No se pudo guardar el Bot ${slot}`,
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Intenta elegir el diseño del bot nuevamente.",
      });
      return false;
    }
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    if (!String(file.type || "").startsWith("image/")) {
      notification.error({
        message: i18nT("profile.formatoNoValido"),
        description: i18nT("profile.seleccionaUnaImagenJpgPngWebpOGif"),
      });
      event.target.value = "";
      return;
    }

    if (file.size > MAX_AVATAR_FILE_SIZE_BYTES) {
      notification.error({
        message: i18nT("profile.fotoMuyGrande"),
        description: i18nT("profile.laFotoDePerfilDebePesarComoMaximo"),
      });
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setAvatarFile(file);
      setAvatarId("");
      setAvatarUrl(String(reader.result || ""));
    };
    reader.readAsDataURL(file);
  };

  const selectAvatarPreset = (preset) => {
    setAvatarId(preset.id);
    setAvatarFile(null);
    setAvatarUrl(preset.src);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const clearAvatar = () => {
    setAvatarId("");
    setAvatarFile(null);
    setAvatarUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resetChanges = () => {
    hydrateFromProfile(profileSnapshotRef.current || profile || user || {});
    notification.info({
      message: i18nT("profile.cambiosDescartados"),
      description: i18nT("profile.seRestauraronLosDatosGuardados"),
    });
  };

  const persistLocalProfile = (nextProfile, nextAvatarUrl) => {
    if (typeof window === "undefined") return;
    const userKey = getUserStorageKey(nextProfile || profile, user);
    window.localStorage.setItem(
      `${PROFILE_STORAGE_PREFIX}${userKey}`,
      JSON.stringify({
        firstName: String(firstName || "").trim(),
        lastName: String(lastName || "").trim(),
        contactEmail: String(contactEmail || "").trim().toLowerCase(),
        avatarUrl: nextAvatarUrl,
        avatar: nextAvatarUrl,
        currentAvatarUrl: nextAvatarUrl,
        profileAvatar: nextAvatarUrl,
      })
    );
    window.dispatchEvent(
      new CustomEvent("xima-profile-change", {
        detail: {
          ...(nextProfile || {}),
          firstName: String(firstName || "").trim(),
          lastName: String(lastName || "").trim(),
          avatarUrl: nextAvatarUrl,
        },
      })
    );
  };

  // Guarda una foto fija del diseño: con un bot seleccionado va a ese bot (solo bloques);
  // sin bot, al diseño de la cuenta.
  const saveDesignConfiguration = async (snapshot) => {
    if (snapshot.slot) {
      return saveBotDesignConfiguration(snapshot);
    }

    const userKey = getUserStorageKey(profileSnapshotRef.current || profile, user);
    const { boardTheme, specialAsset, commonAsset } = snapshot;
    const nextSpecialId = snapshot.blocks.specialBlockId;
    const nextCommonId = snapshot.blocks.commonBlockThemeId;
    const nextBlockMaterialTextureId = snapshot.blocks.blockMaterialTextureId;
    const nextBoardWallTextureId = snapshot.boardWallTextureId;
    const nextGameBackgroundId = snapshot.gameBackgroundId;
    const nextBlockAnimationId = snapshot.blocks.blockAnimationId;

    try {
      const skinResponse = await axios.patch("/auth/profile/skins", {
        specialBlockId: nextSpecialId,
        commonBlockThemeId: nextCommonId,
        blockMaterialTextureId: nextBlockMaterialTextureId,
        boardWallTextureId: nextBoardWallTextureId,
        gameBackgroundId: nextGameBackgroundId,
        blockAnimationId: nextBlockAnimationId,
      });
      const updatedProfile = {
        ...(profileSnapshotRef.current || profile || {}),
        ...(skinResponse?.data?.user || {}),
        specialBlockId: nextSpecialId,
        commonBlockThemeId: nextCommonId,
        blockMaterialTextureId: nextBlockMaterialTextureId,
        boardWallTextureId: nextBoardWallTextureId,
        gameBackgroundId: nextGameBackgroundId,
        blockAnimationId: nextBlockAnimationId,
      };

      if (typeof window !== "undefined") {
        window.localStorage.setItem(GLOBAL_BOARD_THEME_KEY, boardTheme.id);
        window.localStorage.setItem(`${SPECIAL_BLOCK_STORAGE_PREFIX}${userKey}`, nextSpecialId);
        window.localStorage.setItem(`${COMMON_BLOCK_STORAGE_PREFIX}${userKey}`, nextCommonId);
        window.localStorage.setItem(GLOBAL_SPECIAL_BLOCK_KEY, nextSpecialId);
        window.localStorage.setItem(GLOBAL_COMMON_BLOCK_THEME_KEY, nextCommonId);
        window.localStorage.setItem(GLOBAL_BLOCK_MATERIAL_TEXTURE_KEY, nextBlockMaterialTextureId);
        window.localStorage.setItem(GLOBAL_BOARD_WALL_TEXTURE_KEY, nextBoardWallTextureId);
        window.localStorage.setItem(GLOBAL_GAME_BACKGROUND_KEY, nextGameBackgroundId);
        window.localStorage.setItem(GLOBAL_BLOCK_ANIMATION_KEY, nextBlockAnimationId);
        window.localStorage.setItem(
          GLOBAL_SPECIAL_BLOCK_IMAGE_KEY,
          buildBlockPreviewDataUrl(nextSpecialId, specialAsset),
        );
        window.dispatchEvent(new CustomEvent("xima-board-theme-change", { detail: boardTheme }));
        window.dispatchEvent(new CustomEvent("xima-special-block-change", {
          detail: {
            id: nextSpecialId,
            label: nextSpecialId,
            image: buildBlockPreviewDataUrl(nextSpecialId, specialAsset),
            imageName: nextSpecialId,
          },
        }));
        window.dispatchEvent(new CustomEvent("xima-common-block-theme-change", {
          detail: {
            id: nextCommonId,
            label: nextCommonId,
            image: buildBlockPreviewDataUrl(nextCommonId, commonAsset),
            imageName: nextCommonId,
          },
        }));
        window.dispatchEvent(new CustomEvent("xima-material-design-change", {
          detail: {
            blockMaterialTextureId: nextBlockMaterialTextureId,
            boardWallTextureId: nextBoardWallTextureId,
            gameBackgroundId: nextGameBackgroundId,
          },
        }));
        window.dispatchEvent(new CustomEvent("xima-block-animation-design-change", {
          detail: { blockAnimationId: nextBlockAnimationId },
        }));
      }

      profileSnapshotRef.current = updatedProfile;
      setProfile(updatedProfile);
      refreshUserProfile?.().catch(() => {});
      return true;
    } catch (error) {
      notification.error({
        message: "No se pudo guardar",
        description:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Intenta elegir el diseño nuevamente.",
      });
      return false;
    }
  };

  // Guardado automático: cada cambio se guarda solo, en orden, poco después de hacerlo.
  const designSnapshot = useMemo(() => {
    const blocks = {
      specialBlockId: normalizeSkinId(specialBlockId, DEFAULT_SPECIAL_BLOCK_ID),
      commonBlockThemeId: normalizeSkinId(commonBlockId, DEFAULT_COMMON_BLOCK_ID),
      blockMaterialTextureId: normalizeSkinId(blockMaterialTextureId, DEFAULT_MATERIAL_TEXTURE_ID).toLowerCase(),
      blockAnimationId: normalizeSkinId(blockAnimationId, DEFAULT_BLOCK_ANIMATION_ID).toLowerCase(),
    };
    if (botDesignSlot) {
      return { slot: botDesignSlot, blocks, signature: JSON.stringify([botDesignSlot, blocks]) };
    }
    const board = {
      boardThemeId: boardTheme.id,
      boardWallTextureId: normalizeSkinId(boardWallTextureId, DEFAULT_MATERIAL_TEXTURE_ID).toLowerCase(),
      gameBackgroundId: normalizeSkinId(gameBackgroundId, DEFAULT_GAME_BACKGROUND_ID).toLowerCase(),
    };
    return {
      slot: 0,
      blocks,
      ...board,
      boardTheme,
      specialAsset,
      commonAsset,
      signature: JSON.stringify([0, blocks, board]),
    };
  }, [
    blockAnimationId,
    blockMaterialTextureId,
    boardTheme,
    boardWallTextureId,
    botDesignSlot,
    commonAsset,
    commonBlockId,
    gameBackgroundId,
    specialAsset,
    specialBlockId,
  ]);
  const saveDesignConfigurationRef = useRef(saveDesignConfiguration);
  saveDesignConfigurationRef.current = saveDesignConfiguration;
  const autoSaveBaselineRef = useRef(null);
  const autoSavePendingRef = useRef({});
  const autoSaveQueueRef = useRef(Promise.resolve());
  const autoSaveCountRef = useRef(0);
  const [autoSaveState, setAutoSaveState] = useState("idle");
  const [boardCoordFontId, setBoardCoordFontId] = useState(() => {
    const stored = readBoardCoordSetting(BOARD_COORD_FONT_KEY, "classic");
    return BOARD_COORD_FONT_OPTIONS.some((option) => option.id === stored) ? stored : "classic";
  });
  const [boardCoordScale, setBoardCoordScale] = useState(() =>
    clampBoardCoordScale(readBoardCoordSetting(BOARD_COORD_SCALE_KEY, "1"))
  );

  // Se guardan al momento en este navegador, como el tema del tablero.
  const updateBoardCoordFont = (fontId) => {
    setBoardCoordFontId(fontId);
    try {
      window.localStorage.setItem(BOARD_COORD_FONT_KEY, fontId);
    } catch (error) {}
  };
  const updateBoardCoordScale = (value) => {
    const nextScale = clampBoardCoordScale(value);
    setBoardCoordScale(nextScale);
    try {
      window.localStorage.setItem(BOARD_COORD_SCALE_KEY, String(nextScale));
    } catch (error) {}
  };

  const runDesignAutoSave = useCallback((snapshot) => {
    autoSaveCountRef.current += 1;
    setAutoSaveState("saving");
    autoSaveQueueRef.current = autoSaveQueueRef.current
      .then(() => saveDesignConfigurationRef.current(snapshot))
      .catch(() => false)
      .then((ok) => {
        autoSaveCountRef.current -= 1;
        if (!ok) setAutoSaveState("error");
        else if (autoSaveCountRef.current === 0) setAutoSaveState("saved");
      });
  }, []);

  useEffect(() => {
    if (loading) return;
    const baseline = autoSaveBaselineRef.current;
    // Al entrar o al cambiar de bot solo se toma nota de lo que hay; no se guarda.
    if (!baseline || baseline.slot !== designSnapshot.slot) {
      autoSaveBaselineRef.current = { slot: designSnapshot.slot, signature: designSnapshot.signature };
      return;
    }
    if (baseline.signature === designSnapshot.signature) return;
    autoSaveBaselineRef.current = { slot: designSnapshot.slot, signature: designSnapshot.signature };

    const pending = autoSavePendingRef.current;
    const previous = pending[designSnapshot.slot];
    if (previous) clearTimeout(previous.timer);
    const snapshot = designSnapshot;
    pending[snapshot.slot] = {
      snapshot,
      timer: setTimeout(() => {
        delete pending[snapshot.slot];
        runDesignAutoSave(snapshot);
      }, 400),
    };
  }, [designSnapshot, loading, runDesignAutoSave]);

  // Si se sale de Modo Diseñar con un cambio aún sin enviar, se envía ya.
  useEffect(
    () => () => {
      const pending = autoSavePendingRef.current;
      Object.keys(pending).forEach((slot) => {
        clearTimeout(pending[slot].timer);
        runDesignAutoSave(pending[slot].snapshot);
        delete pending[slot];
      });
    },
    [runDesignAutoSave]
  );

  const saveProfile = async () => {
    if (saving) return;

    const normalizedFirstName = String(firstName || "").trim();
    const normalizedLastName = String(lastName || "").trim();
    const normalizedEmail = String(contactEmail || "").trim().toLowerCase();
    const wantsPasswordChange =
      String(currentPassword || "").trim() || String(newPassword || "").trim() || String(confirmPassword || "").trim();

    if (!normalizedFirstName) {
      notification.error({ message: i18nT("profile.nombreRequerido"), description: i18nT("profile.escribeTuNombreAntesDeGuardar") });
      return;
    }
    if (!normalizedLastName) {
      notification.error({ message: i18nT("profile.apellidoRequerido"), description: i18nT("profile.escribeTuApellidoAntesDeGuardar") });
      return;
    }
    if (normalizedEmail && !PROFILE_CONTACT_EMAIL_PATTERN.test(normalizedEmail)) {
      notification.error({ message: i18nT("profile.correoNoValido"), description: i18nT("profile.revisaElCorreoOpcional") });
      return;
    }
    if (wantsPasswordChange) {
      if (!String(currentPassword || "").trim() || !String(newPassword || "").trim()) {
        notification.error({
          message: i18nT("profile.contrasenaIncompleta"),
          description: i18nT("profile.escribeLaContrasenaActualYLaNuevaContrasena"),
        });
        return;
      }
      if (String(newPassword || "").length < 6) {
        notification.error({
          message: i18nT("profile.contrasenaMuyCorta"),
          description: i18nT("profile.laNuevaContrasenaDebeTenerAlMenosCaracteres"),
        });
        return;
      }
      if (newPassword !== confirmPassword) {
        notification.error({
          message: i18nT("profile.contrasenasDiferentes"),
          description: i18nT("profile.laConfirmacionDebeCoincidirConLaNuevaContras5261e2"),
        });
        return;
      }
    }

    const userKey = getUserStorageKey(profile, user);
    const nextSpecialId = normalizeSkinId(specialBlockId, DEFAULT_SPECIAL_BLOCK_ID);
    const nextCommonId = normalizeSkinId(commonBlockId, DEFAULT_COMMON_BLOCK_ID);
    const nextBlockMaterialTextureId = normalizeSkinId(
      blockMaterialTextureId,
      DEFAULT_MATERIAL_TEXTURE_ID
    ).toLowerCase();
    const nextBoardWallTextureId = normalizeSkinId(
      boardWallTextureId,
      DEFAULT_MATERIAL_TEXTURE_ID
    ).toLowerCase();
    const nextGameBackgroundId = normalizeSkinId(
      gameBackgroundId,
      DEFAULT_GAME_BACKGROUND_ID
    ).toLowerCase();
    const nextBlockAnimationId = normalizeSkinId(
      blockAnimationId,
      DEFAULT_BLOCK_ANIMATION_ID
    ).toLowerCase();

    try {
      setSaving(true);
      const personalPayload = {
        firstName: normalizedFirstName,
        lastName: normalizedLastName,
        contactEmail: normalizedEmail,
        ...(avatarFile ? {} : { avatarUrl }),
        ...(wantsPasswordChange ? { currentPassword, newPassword } : {}),
      };

      const personalResponse = await axios.patch("/auth/profile/personal", personalPayload);
      let updatedProfile = personalResponse?.data?.user || personalResponse?.data || {};
      let nextAvatarUrl = avatarUrl;

      if (avatarFile) {
        const formData = new FormData();
        formData.append("avatar", avatarFile);
        const avatarResponse = await axios.patch("/auth/profile/avatar", formData);
        updatedProfile = avatarResponse?.data?.user || updatedProfile;
        nextAvatarUrl = getProfileAvatar(updatedProfile) || avatarUrl;
        setAvatarUrl(nextAvatarUrl);
        setAvatarFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }

      const skinResponse = await axios.patch("/auth/profile/skins", {
        specialBlockId: nextSpecialId,
        commonBlockThemeId: nextCommonId,
        blockMaterialTextureId: nextBlockMaterialTextureId,
        boardWallTextureId: nextBoardWallTextureId,
        gameBackgroundId: nextGameBackgroundId,
        blockAnimationId: nextBlockAnimationId,
      });
      updatedProfile = {
        ...updatedProfile,
        ...(skinResponse?.data?.user || {}),
        specialBlockId: nextSpecialId,
        commonBlockThemeId: nextCommonId,
        blockMaterialTextureId: nextBlockMaterialTextureId,
        boardWallTextureId: nextBoardWallTextureId,
        gameBackgroundId: nextGameBackgroundId,
        blockAnimationId: nextBlockAnimationId,
      };

      if (typeof window !== "undefined") {
        window.localStorage.setItem(GLOBAL_BOARD_THEME_KEY, boardTheme.id);
        window.localStorage.setItem(`${SPECIAL_BLOCK_STORAGE_PREFIX}${userKey}`, nextSpecialId);
        window.localStorage.setItem(`${COMMON_BLOCK_STORAGE_PREFIX}${userKey}`, nextCommonId);
        window.localStorage.setItem(GLOBAL_SPECIAL_BLOCK_KEY, nextSpecialId);
        window.localStorage.setItem(GLOBAL_COMMON_BLOCK_THEME_KEY, nextCommonId);
        window.localStorage.setItem(
          GLOBAL_BLOCK_MATERIAL_TEXTURE_KEY,
          nextBlockMaterialTextureId
        );
        window.localStorage.setItem(
          GLOBAL_BOARD_WALL_TEXTURE_KEY,
          nextBoardWallTextureId
        );
        window.localStorage.setItem(GLOBAL_GAME_BACKGROUND_KEY, nextGameBackgroundId);
        window.localStorage.setItem(GLOBAL_BLOCK_ANIMATION_KEY, nextBlockAnimationId);
        window.localStorage.setItem(
          GLOBAL_SPECIAL_BLOCK_IMAGE_KEY,
          buildBlockPreviewDataUrl(nextSpecialId, specialAsset)
        );
        window.dispatchEvent(new CustomEvent("xima-board-theme-change", { detail: boardTheme }));
        window.dispatchEvent(
          new CustomEvent("xima-special-block-change", {
            detail: {
              id: nextSpecialId,
              label: nextSpecialId,
              image: buildBlockPreviewDataUrl(nextSpecialId, specialAsset),
              imageName: nextSpecialId,
            },
          })
        );
        window.dispatchEvent(
          new CustomEvent("xima-common-block-theme-change", {
            detail: {
              id: nextCommonId,
              label: nextCommonId,
              image: buildBlockPreviewDataUrl(nextCommonId, commonAsset),
              imageName: nextCommonId,
            },
          })
        );
        window.dispatchEvent(
          new CustomEvent("xima-material-design-change", {
            detail: {
              blockMaterialTextureId: nextBlockMaterialTextureId,
              boardWallTextureId: nextBoardWallTextureId,
              gameBackgroundId: nextGameBackgroundId,
            },
          })
        );
        window.dispatchEvent(
          new CustomEvent("xima-block-animation-design-change", {
            detail: { blockAnimationId: nextBlockAnimationId },
          })
        );
      }

      persistLocalProfile(updatedProfile, nextAvatarUrl);
      profileSnapshotRef.current = { ...(profileSnapshotRef.current || {}), ...updatedProfile, avatarUrl: nextAvatarUrl };
      setProfile(profileSnapshotRef.current);
      refreshUserProfile?.().catch(() => {});
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      notification.success({
        message: i18nT("profile.perfilGuardado"),
        description: i18nT("profile.tuFotoDatosYDisenoSeAplicaronCorrectamente"),
      });
    } catch (error) {
      const code = String(error?.response?.data?.code || "").trim();
      notification.error({
        message: code.includes("SKIN") ? "Skin bloqueado" : "No se pudo guardar",
        description:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Intenta guardar el perfil nuevamente.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSkinSelection = (blockType, item, updateSelection) => {
    const skinId = normalizeSkinId(item?.id, "");
    if (!skinId) return;

    const ownership = resolveOwnership(blockType, skinId);
    if (ownership.unlocked) {
      updateSelection(skinId);
      if (blockType === BLOCK_TYPE_COMMON) {
        setBlockMaterialTextureId(DEFAULT_MATERIAL_TEXTURE_ID);
        setBlockAnimationId(DEFAULT_BLOCK_ANIMATION_ID);
      }
      return;
    }

    const skinLabel = String(item?.label || skinId);
    const safeLabel = skinLabel.trim() || skinId;
    if (ownership.accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER) {
      const requiredMode = normalizeTopWinnerModeForSkinUnlock(ownership.requiredMode);
      notification.warning({
        message: i18nT("profile.skinDePremioNoDesbloqueado"),
        description: requiredMode
          ? designText("earnedTopRequired", {
              label: safeLabel,
              mode: getDesignModeName(language, requiredMode),
            })
          : designText("skinUnavailable"),
      });
      return;
    }

    Modal.confirm({
      className: "xima-skin-purchase-confirm",
      title: i18nT("profile.skinDePagoBloqueado"),
      content: designText("buySkinQuestion", { label: safeLabel }),
      okText: designText("buy"),
      cancelText: designText("reject"),
      onOk: () => {
        const nextType =
          blockType === BLOCK_TYPE_COMMON ? BLOCK_TYPE_COMMON : BLOCK_TYPE_SPECIAL;
        const nextProductId = String(ownership.productId || "").trim();
        const params = new URLSearchParams({ fromLobbySource: "design", type: nextType });
        if (nextProductId) {
          params.set("product", nextProductId);
        }
        navigate(`/game/store?${params.toString()}`);
      },
      onCancel: () => {},
    });
  };

  const handleMaterialSelection = (option, updateSelection) => {
    const textureId = normalizeSkinId(option?.id, DEFAULT_MATERIAL_TEXTURE_ID).toLowerCase();
    if (textureId === DEFAULT_MATERIAL_TEXTURE_ID || option?.isColor || option?.isFlag) {
      updateSelection(textureId);
      return;
    }
    const ownership = resolveOwnership(BLOCK_TYPE_MATERIAL, textureId);
    if (ownership.unlocked) {
      updateSelection(textureId);
      return;
    }
    Modal.confirm({
      className: "xima-skin-purchase-confirm",
      title: "Textura bloqueada",
      content: `${option?.label || textureId} debe comprarse en la tienda por 5,000 X.`,
      okText: designText("buy"),
      cancelText: designText("reject"),
      onOk: () => {
        const params = new URLSearchParams({
          fromLobbySource: "design",
          type: BLOCK_TYPE_MATERIAL,
          product: String(ownership.productId || option?.productId || ""),
        });
        navigate(`/game/store?${params.toString()}`);
      },
    });
  };

  const handleBlockMaterialSelection = (option) => {
    const textureId = normalizeSkinId(option?.id, DEFAULT_MATERIAL_TEXTURE_ID).toLowerCase();
    const ownership = resolveOwnership(BLOCK_TYPE_MATERIAL, textureId);
    if (textureId !== DEFAULT_MATERIAL_TEXTURE_ID && !ownership.unlocked) {
      handleMaterialSelection(option, setBlockMaterialTextureId);
      return;
    }
    setBlockMaterialTextureId(textureId);
    setCommonBlockId(DEFAULT_COMMON_BLOCK_ID);
    setBlockAnimationId(DEFAULT_BLOCK_ANIMATION_ID);
  };

  const handleAnimationSelection = (option) => {
    const animationId = normalizeSkinId(option?.id, DEFAULT_BLOCK_ANIMATION_ID).toLowerCase();
    if (animationId === DEFAULT_BLOCK_ANIMATION_ID) {
      setBlockAnimationId(animationId);
      setCommonBlockId(DEFAULT_COMMON_BLOCK_ID);
      setBlockMaterialTextureId(DEFAULT_MATERIAL_TEXTURE_ID);
      return;
    }
    const ownership = resolveOwnership(BLOCK_TYPE_ANIMATION, animationId);
    if (ownership.unlocked) {
      setBlockAnimationId(animationId);
      setCommonBlockId(DEFAULT_COMMON_BLOCK_ID);
      setBlockMaterialTextureId(DEFAULT_MATERIAL_TEXTURE_ID);
      return;
    }
    Modal.confirm({
      className: "xima-skin-purchase-confirm",
      title: "Animacion bloqueada",
      content: `${option?.label || animationId} debe comprarse en la tienda por 5,000 X.`,
      okText: designText("buy"),
      cancelText: designText("reject"),
      onOk: () => {
        const params = new URLSearchParams({
          fromLobbySource: "design",
          type: BLOCK_TYPE_ANIMATION,
          product: String(ownership.productId || option?.productId || ""),
        });
        navigate(`/game/store?${params.toString()}`);
      },
    });
  };

  const renderProfileWorkspace = () => (
    <section className="xima-profile-design-content xima-profile-design-profile-workspace">
      <div className="xima-profile-design-top">
        <article id="xima-profile-design-profile" className="xima-profile-design-panel xima-profile-design-profile-panel">
              <div className="xima-profile-design-profile-head">
                <img src={avatarUrl || AVATAR_PRESETS[1].src} alt={displayName} />
                <div>
                  <h2>{designText("playerProfile")}</h2>
                  <strong>{displayName}</strong>
                  <span>{String(profile?.username || profile?.loginIdentifier || user?.username || designText("playerFallback")).trim()}</span>
                  <p>{designText("profileDescription")}</p>
                </div>
              </div>

              <div className="xima-profile-design-divider" />
              <h3>{designText("personalInformation")}</h3>
              <div className="xima-profile-design-form-grid">
                <label>
                  {designText("firstName")}
                  <input value={firstName} onChange={(event) => setFirstName(event.target.value)} placeholder={designText("firstNamePlaceholder")} />
                </label>
                <label>
                  {designText("lastName")}
                  <input value={lastName} onChange={(event) => setLastName(event.target.value)} placeholder={designText("lastNamePlaceholder")} />
                </label>
                <label>
                  {designText("emailOptional")}
                  <input value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} placeholder={i18nT("profile.correoEjemploCom")} />
                </label>
              </div>

              <div className="xima-profile-design-divider" />
              <h3>{designText("changePasswordOptional")}</h3>
              <div className="xima-profile-design-password-grid">
                <label>
                  {designText("current")}
                  <input
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    type="password"
                    placeholder={designText("currentPassword")}
                    autoComplete="new-password"
                    name="xima-current-password"
                    readOnly
                    onFocus={(event) => {
                      event.currentTarget.readOnly = false;
                    }}
                  />
                </label>
                <label>
                  {designText("newPasswordLabel")}
                  <input
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    type="password"
                    placeholder={designText("newPassword")}
                    autoComplete="new-password"
                    name="xima-new-password"
                  />
                </label>
                <label>
                  {designText("confirm")}
                  <input
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    type="password"
                    placeholder={designText("repeatPassword")}
                    autoComplete="new-password"
                    name="xima-confirm-password"
                  />
                </label>
              </div>
        </article>

        <div className="xima-profile-design-top-right">
          <article className="xima-profile-design-panel xima-profile-design-upload-panel">
                <h3>{designText("uploadPhoto")}</h3>
                <label className="xima-profile-design-file-button">
                  {designText("chooseFile")}
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} />
                </label>
                <p>{designText("recommendedPhotoFormat")}</p>

                <h3>{designText("presetAvatars")}</h3>
                <div className="xima-profile-design-avatar-grid">
                  {AVATAR_PRESETS.map((preset) => {
                    const selected =
                      avatarId === preset.id &&
                      stripAssetVersion(avatarUrl) === stripAssetVersion(preset.src);
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        className={selected ? "is-selected" : ""}
                        onClick={() => selectAvatarPreset(preset)}
                        aria-pressed={selected}
                      >
                        <img
                          src={preset.src}
                          alt={preset.name}
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                        <strong>{preset.name}</strong>
                      </button>
                    );
                  })}
                </div>
          </article>
        </div>
      </div>

      <footer className="xima-profile-design-actions">
            <button type="button" className="xima-profile-design-no-photo" onClick={clearAvatar} disabled={saving || loading}>
              <IoBanOutline aria-hidden="true" />
              {designText("noPhoto")}
            </button>
            <button type="button" className="xima-profile-design-reset" onClick={resetChanges} disabled={saving || loading}>
              <IoRefreshOutline aria-hidden="true" />
              {designText("reset")}
            </button>
            <button type="button" className="xima-profile-design-cancel" onClick={() => navigate("/game/lobby")} disabled={saving}>
              {designText("cancel")}
            </button>
            <button type="button" className="xima-profile-design-save" onClick={saveProfile} disabled={saving || loading}>
              <IoSaveOutline aria-hidden="true" />
              {saving ? designText("saving") : designText("saveProfile")}
            </button>
      </footer>
    </section>
  );

  // ---------------------------------------------------------------------
  // Tablero: diseño por partes
  // ---------------------------------------------------------------------

  const teamColor = TEAM_COLORS.find((entry) => entry.id === teamColorId) || TEAM_COLORS[0];

  const partMaterials = useMemo(() => {
    const map = {};
    TEXTURE_MATERIALS.forEach((entry) => {
      map[entry.id] = entry;
    });
    FLAG_CATALOG.forEach((flag) => {
      map[`flag:flag-${flag.key}`] = { image: resolveVersionedPublicAssetPath(`flags/walls/flag-${flag.key}.png`) };
    });
    return map;
  }, []);

  const allBoardPresets = useMemo(
    () => [
      ...BOARD_PRESETS,
      ...myBoards.map((entry) => ({
        id: entry.id,
        label: entry.label,
        subtitle: "Mi tablero",
        tags: ["mine"],
        themeId: entry.themeId || "classic",
        wallTextureId: entry.wallTextureId,
        parts: entry.parts,
        isMine: true,
      })),
    ],
    [myBoards],
  );

  const isPresetInUse = (preset) =>
    sameBoardParts(preset.parts, boardDesign) &&
    (!isOrigPart(boardDesign.dark) || (preset.themeId || "classic") === boardTheme.id) &&
    (!isOrigPart(boardDesign.wall) || !preset.wallTextureId || preset.wallTextureId === boardWallTextureId);

  const updateBoardDesign = (patch) => {
    setBoardDesign((current) => {
      const next = normalizeBoardDesign({ ...current, ...patch });
      if (!patch.preset) {
        const match = BOARD_PRESETS.find((preset) => sameBoardParts(preset.parts, next));
        next.preset = match ? match.id : "custom";
      }
      writeStoredBoardDesign(next);
      return next;
    });
  };

  // El juego lee el diseño al momento (localStorage), igual que el tema antiguo.
  useEffect(() => {
    writeStoredBoardDesign(boardDesign);
    window.dispatchEvent(new CustomEvent("xima-board-design-change", { detail: boardDesign }));
  }, [boardDesign]);

  const applyBoardPreset = (preset) => {
    if (!preset) return;
    updateBoardDesign({ ...preset.parts, preset: preset.id });
    if (preset.themeId) setBoardThemeId(preset.themeId);
    if (preset.wallTextureId) setBoardWallTextureId(preset.wallTextureId);
  };

  const saveAsMyBoard = () => {
    const next = [
      {
        id: `mio-${Date.now().toString(36)}`,
        label: `Mi tablero ${myBoards.length + 1}`,
        parts: normalizeBoardDesign(boardDesign),
        themeId: boardTheme.id,
        wallTextureId: boardWallTextureId,
      },
      ...myBoards,
    ];
    setMyBoards(next);
    writeMyBoards(next);
    setBoardFilter("mine");
    notification.success({
      message: "Guardado en Mis tableros",
      description: `"${next[0].label}" ya aparece en Tableros completos.`,
    });
  };

  const removeMyBoard = (boardId) => {
    const next = myBoards.filter((entry) => entry.id !== boardId);
    setMyBoards(next);
    writeMyBoards(next);
  };

  const resetBoardStudio = () => {
    applyBoardPreset(BOARD_PRESETS[0]);
    setBoardWallTextureId(DEFAULT_MATERIAL_TEXTURE_ID);
    updateBoardCoordFont("classic");
    updateBoardCoordScale(1);
    notification.info({ message: "Tablero restablecido", description: "Volviste al Clásico Xima." });
  };

  const filteredBoardPresets = useMemo(
    () =>
      allBoardPresets.filter((preset) =>
        boardFilter === "all" || (preset.tags || []).includes(boardFilter),
      ),
    [allBoardPresets, boardFilter],
  );

  // Al entrar, el carrusel se centra en el tablero que está en uso.
  useEffect(() => {
    if (loading || boardCarouselSyncedRef.current) return;
    const inUse = filteredBoardPresets.findIndex((preset) => isPresetInUse(preset));
    if (inUse >= 0) setBoardCarouselIndex(inUse);
    boardCarouselSyncedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, filteredBoardPresets]);

  const legacyDarkThemes = BOARD_THEMES.map((theme) => ({
    id: `theme:${theme.id}`,
    themeId: theme.id,
    label: theme.id === "classic" ? "Verde clásico" : theme.id === "emerald" ? "Madera clásica" : theme.label,
    type: "colores",
    swatch: theme.previewBackground || `linear-gradient(145deg, ${theme.glow}, ${theme.dark})`,
    status: "free",
  }));

  const textureOptions = TEXTURE_MATERIALS.map((entry) => ({
    id: entry.id,
    label: entry.label,
    type: entry.type,
    image: entry.thumb,
    status: "free",
  }));

  const colorOptions = COLOR_MATERIALS.map((entry) => ({
    id: entry.id,
    label: entry.label,
    type: "colores",
    swatch: `linear-gradient(145deg, ${entry.hex}, ${entry.hex}cc 55%, #0006)`,
    status: "free",
  }));

  const legacyWallType = (id) => {
    if (/lava|basalt|grass/.test(id)) return "naturaleza";
    if (/wood/.test(id)) return "madera";
    if (/paving|tiles|onyx|brick|metal/.test(id)) return "piedra";
    return "colores";
  };

  const buildPartOptions = (part) => {
    if (part === "light") {
      return [
        { id: "orig", label: "Blanco clásico", type: "colores", swatch: "linear-gradient(145deg,#ffffff,#ece6e6 60%,#bdb6b6)", status: "free" },
        ...textureOptions,
        ...colorOptions,
      ];
    }
    if (part === "dark") {
      // Las banderas no van en las casillas (solo en paredes y bloques).
      return [...legacyDarkThemes, ...textureOptions, ...colorOptions];
    }
    if (part === "wall") {
      return [
        { id: "legacy:default", legacyId: "default", label: "Cristal clásico", type: "colores", swatch: "linear-gradient(145deg,#d8f6ff55,#7fd4ee66 50%,#1b4a6a88)", status: "free" },
        ...textureOptions,
        ...WALL_COLOR_OPTIONS.map((option) => ({ id: `legacy:${option.id}`, legacyId: option.id, legacyOption: option, label: option.label, type: "colores", swatch: `linear-gradient(145deg, ${option.hex}, #0006)`, status: "free" })),
        ...MATERIAL_TEXTURE_OPTIONS.filter((option) => option.id !== DEFAULT_MATERIAL_TEXTURE_ID).map((option) => {
          const ownership = resolveOwnership(BLOCK_TYPE_MATERIAL, option.id);
          return {
            id: `legacy:${option.id}`,
            legacyId: option.id,
            legacyOption: option,
            label: option.label,
            type: legacyWallType(option.id),
            image: option.image,
            status: ownership.unlocked ? "won" : "shop",
            price: "5000",
          };
        }),
        ...WALL_FLAG_OPTIONS.map((option) => ({ id: `legacy:${option.id}`, legacyId: option.id, legacyOption: option, label: getSkinItemLabel(language, option), type: "banderas", image: option.image, status: "free" })),
      ];
    }
    if (part === "trim") {
      return TRIM_OPTIONS.map((option) => ({ id: option.id, label: option.label, type: "colores", swatch: option.swatch, status: "free" }));
    }
    return [];
  };

  const currentPartValue = (part) => {
    if (part === "dark" && isOrigPart(boardDesign.dark)) return `theme:${boardTheme.id}`;
    if (part === "wall" && isOrigPart(boardDesign.wall)) return `legacy:${boardWallTextureId || DEFAULT_MATERIAL_TEXTURE_ID}`;
    return boardDesign[part];
  };

  const selectPartOption = (part, option) => {
    if (!option) return;
    if (option.status === "lock") return;
    if (part === "dark" && option.themeId) {
      setBoardThemeId(option.themeId);
      updateBoardDesign({ dark: "orig" });
      return;
    }
    if (part === "wall" && option.legacyId) {
      if (option.legacyId === "default") {
        setBoardWallTextureId(DEFAULT_MATERIAL_TEXTURE_ID);
        updateBoardDesign({ wall: "orig" });
        return;
      }
      // Texturas antiguas de pared: mismas reglas de tienda que antes.
      handleMaterialSelection(option.legacyOption, (textureId) => {
        setBoardWallTextureId(textureId);
        updateBoardDesign({ wall: "orig" });
      });
      return;
    }
    updateBoardDesign({ [part]: option.id });
  };

  const PART_DEFS = [
    { id: "light", number: 1, label: "Casillas claras", heading: "casillas claras" },
    { id: "dark", number: 2, label: "Casillas oscuras", heading: "casillas oscuras" },
    { id: "wall", number: 3, label: "Paredes", heading: "paredes" },
    { id: "labels", number: 4, label: "Letras y números", heading: "letras y números" },
    { id: "trim", number: 5, label: "Bordes y molduras", heading: "molduras" },
  ];

  const describePart = (part) => {
    if (part === "labels") {
      const font = BOARD_COORD_FONT_OPTIONS.find((option) => option.id === boardCoordFontId) || BOARD_COORD_FONT_OPTIONS[0];
      const trimOption = getTrimOption(boardDesign.trim);
      const size = boardCoordScale >= 1.25 ? "tamaño grande" : boardCoordScale <= 0.85 ? "tamaño pequeño" : "tamaño normal";
      return {
        label: `${isOrigPart(boardDesign.trim) ? font.label : trimOption.label.split(" ")[0]} · ${size}`,
        swatch: isOrigPart(boardDesign.trim) ? "linear-gradient(135deg,#fff7a8,#e6c84a)" : trimOption.swatch,
        text: "A1",
      };
    }
    const value = currentPartValue(part);
    const option = buildPartOptions(part).find((entry) => entry.id === value);
    return {
      label: option?.label || "Clásico",
      swatch: option?.swatch,
      image: option?.image,
    };
  };

  const activePartDef = PART_DEFS.find((entry) => entry.id === activePart) || PART_DEFS[2];
  const activePartOptions = activePart === "labels" ? [] : buildPartOptions(activePart);
  const visiblePartOptions = activePartOptions.filter(
    (option) =>
      (materialTypeFilter === "all" || option.type === materialTypeFilter) &&
      (materialStatusFilter === "all" || option.status === materialStatusFilter),
  );
  const selectedPartValue = activePart === "labels" ? "" : currentPartValue(activePart);

  const selectBoardPart = (part) => {
    setActivePart(part);
    setMaterialTypeFilter("all");
    setMaterialStatusFilter("all");
  };

  const renderSwatch = (info, className = "") => (
    <span
      className={`xima-studio-swatch ${className}`}
      style={
        info?.image
          ? { backgroundImage: `url("${info.image}")` }
          : info?.swatch
          ? { background: info.swatch }
          : undefined
      }
    >
      {info?.text ? <b>{info.text}</b> : null}
    </span>
  );

  const renderBoardCard = (preset) => {
    const inUse = isPresetInUse(preset);
    const thumb = preset.thumb;
    return {
      key: preset.id,
      label: preset.label,
      preset,
      content: (
        <>
          {thumb ? (
            <img src={thumb} alt="" style={preset.thumbFilter ? { filter: preset.thumbFilter } : undefined} draggable="false" />
          ) : (
            <span
              className="xima-studio-card-checker"
              style={{
                "--checker-a": describeTile(preset.parts.light, true),
                "--checker-b": describeTile(preset.parts.dark, false),
              }}
            />
          )}
          <StudioBadge status={inUse ? "on" : "free"} className="xima-studio-card-badge" />
          {preset.isMine ? (
            <button
              type="button"
              className="xima-studio-card-remove"
              onClick={(event) => {
                event.stopPropagation();
                removeMyBoard(preset.id);
              }}
              aria-label={`Borrar ${preset.label}`}
            >
              ×
            </button>
          ) : null}
          <div className="xima-studio-card-name">
            {preset.label}
            <small>{preset.subtitle}</small>
          </div>
        </>
      ),
    };
  };

  function describeTile(part, light) {
    if (isOrigPart(part)) return light ? "#f4f1ef" : boardTheme.dark;
    const texture = getTextureMaterial(part);
    if (texture) return `url("${texture.thumb}") center/cover`;
    const color = getColorMaterial(part);
    if (color) return color.hex;
    if (part.startsWith("flag:")) return `url("${resolveVersionedPublicAssetPath(`flags/walls/${part.slice(5)}.png`)}") center/cover`;
    return "#888";
  }

  const renderBoardPreview = (options = {}) => (
    <DesignModePreview
      boardTheme={boardTheme}
      wallTexture={selectedWallTexture}
      background={selectedGameBackground}
      blockMaterial={selectedBlockMaterial}
      blockAnimation={selectedBlockAnimation}
      commonAsset={commonAsset}
      specialAsset={specialAsset}
      commonBlockId={commonBlockId}
      specialBlockId={specialBlockId}
      boardDesign={boardDesign}
      coordFont={boardCoordFontId}
      coordScale={boardCoordScale}
      commonStyle={getBlockStyle(commonBlockId) ? commonBlockId : ""}
      specialStyle={getBlockStyle(specialBlockId) ? specialBlockId : ""}
      teamColor={teamColor.game}
      assetUrl={studioAsset}
      partMaterials={partMaterials}
      {...options}
    />
  );

  const toggleStageFullscreen = () => {
    const element = boardStageRef.current;
    if (!element) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else element.requestFullscreen?.().catch(() => {});
  };

  const renderBoardTab = () => (
    <section className="xima-studio-board">
      <div className="xima-studio-board-left">
        <article ref={boardStageRef} className="xima-studio-panel xima-studio-stage">
          <div className="xima-studio-stage-head">
            <div>
              <h2 className="xima-studio-h">Vista previa en vivo</h2>
              <p className="xima-studio-sub">Arrastra para girar · rueda para acercar</p>
            </div>
            <span className="xima-studio-hint">👆 Toca una parte del tablero para cambiarla</span>
          </div>
          <div className="xima-studio-stage-view">
            {renderBoardPreview({
              showPins: true,
              activePart,
              onPartSelect: selectBoardPart,
              autoRotate: previewAutoRotate,
              resetSignal: previewResetSignal,
            })}
          </div>
          <div className="xima-studio-stage-controls">
            <button type="button" onClick={() => setPreviewResetSignal((value) => value + 1)} title="Vista 3D inicial">
              3D
            </button>
            <button type="button" onClick={toggleStageFullscreen} title="Pantalla completa">
              <StudioIcon name="expand" size={16} />
            </button>
            <button
              type="button"
              className={previewAutoRotate ? "is-on" : ""}
              onClick={() => setPreviewAutoRotate((value) => !value)}
              title="Girar solo"
            >
              <StudioIcon name="rot" size={16} />
            </button>
          </div>
        </article>

        <article className="xima-studio-panel xima-studio-shelf">
          <div className="xima-studio-row">
            <div>
              <h2 className="xima-studio-h">Tableros completos</h2>
              <p className="xima-studio-sub">Desliza para elegir un tablero ya diseñado, como un catálogo de discos</p>
            </div>
            <span className="xima-studio-spacer" />
            <StudioChips
              options={BOARD_FILTERS}
              value={boardFilter}
              onChange={(value) => {
                setBoardFilter(value);
                setBoardCarouselIndex(0);
              }}
            />
          </div>
          {filteredBoardPresets.length ? (
            <CoverFlow
              items={filteredBoardPresets.map(renderBoardCard)}
              index={boardCarouselIndex}
              onIndexChange={setBoardCarouselIndex}
              onPick={(item) => applyBoardPreset(item.preset)}
              cardWidth={330}
              cardHeight={228}
              gap={200}
              sideScale={0.8}
              showDots
              className="is-boards"
            />
          ) : (
            <p className="xima-studio-empty">
              Aún no tienes tableros guardados. Personaliza las partes y pulsa “Guardar como Mi tablero”.
            </p>
          )}
        </article>
      </div>

      <article className="xima-studio-panel xima-studio-parts-panel">
        <h2 className="xima-studio-h">Personaliza cada parte</h2>
        <p className="xima-studio-sub">Elige una parte y luego su material. Todo en un mismo lugar.</p>
        <div className="xima-studio-parts">
          {PART_DEFS.map((part) => {
            const info = describePart(part.id);
            return (
              <button
                key={part.id}
                type="button"
                className={`xima-studio-part${activePart === part.id ? " is-on" : ""}`}
                onClick={() => selectBoardPart(part.id)}
                aria-pressed={activePart === part.id}
              >
                <span className="xima-studio-part-number">{part.number}</span>
                {renderSwatch(info)}
                <span className="xima-studio-part-text">
                  <b>{part.label}</b>
                  <small>{info.label}</small>
                </span>
                <StudioIcon name="chev" />
              </button>
            );
          })}
        </div>

        <div className="xima-studio-materials">
          {activePart === "labels" ? (
            <>
              <div className="xima-studio-mat-title">
                Estilo de las <span>letras y números</span>
              </div>
              <p className="xima-studio-sub">
                {isOrigPart(boardDesign.trim)
                  ? "Con el borde de cristal clásico las letras flotan junto al tablero."
                  : `Con molduras de ${getTrimOption(boardDesign.trim).label.toLowerCase()} van pintadas en las paredes de cada piso.`}
              </p>
              <div className="xima-studio-grid is-fonts">
                {BOARD_COORD_FONT_OPTIONS.map((option) => {
                  const selected = boardCoordFontId === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={`xima-studio-item is-font${selected ? " is-selected" : ""}`}
                      onClick={() => updateBoardCoordFont(option.id)}
                      aria-pressed={selected}
                    >
                      <span className={`xima-studio-font-sample xima-design-coord-font is-${option.id}`}>A1</span>
                      <StudioBadge status={selected ? "on" : "free"} className="xima-studio-item-badge" />
                      <span className="xima-studio-item-name">{option.label}</span>
                    </button>
                  );
                })}
              </div>
              <label className="xima-studio-range">
                <span>Tamaño</span>
                <input
                  type="range"
                  min={BOARD_COORD_SCALE_MIN}
                  max={BOARD_COORD_SCALE_MAX}
                  step="0.05"
                  value={boardCoordScale}
                  onChange={(event) => updateBoardCoordScale(event.target.value)}
                  aria-label="Tamaño de las letras y números del tablero"
                />
                <b>{Math.round(boardCoordScale * 100)}%</b>
              </label>
            </>
          ) : (
            <>
              <div className="xima-studio-mat-title">
                Material de las <span>{activePartDef.heading}</span>
                <small> · {activePartOptions.length} disponibles</small>
              </div>
              {activePart !== "trim" ? (
                <>
                  <StudioChips options={MATERIAL_TYPE_FILTERS} value={materialTypeFilter} onChange={setMaterialTypeFilter} />
                  <StudioChips options={STATUS_FILTERS} value={materialStatusFilter} onChange={setMaterialStatusFilter} small dots />
                </>
              ) : null}
              <div className="xima-studio-grid">
                {visiblePartOptions.map((option) => {
                  const selected = selectedPartValue === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={`xima-studio-item${selected ? " is-selected" : ""}`}
                      onClick={() => selectPartOption(activePart, option)}
                      aria-pressed={selected}
                      title={option.label}
                    >
                      {renderSwatch(option, "xima-studio-item-image")}
                      <StudioBadge
                        status={selected ? "on" : option.status}
                        price={option.price}
                        requirement={option.requirement}
                        className="xima-studio-item-badge"
                      />
                      <span className="xima-studio-item-name">{option.label}</span>
                    </button>
                  );
                })}
                {!visiblePartOptions.length ? <p className="xima-studio-empty">No hay materiales con este filtro.</p> : null}
              </div>
            </>
          )}
        </div>

        <StudioLegend />
        <div className="xima-studio-foot">
          <button type="button" className="xima-studio-btn" onClick={resetBoardStudio}>
            <StudioIcon name="rot" size={16} /> Restablecer
          </button>
          <button type="button" className="xima-studio-btn is-primary" onClick={saveAsMyBoard}>
            Guardar como “Mi tablero”
          </button>
        </div>
      </article>
    </section>
  );

  // ---------------------------------------------------------------------
  // Bloques: catálogo común / especial
  // ---------------------------------------------------------------------

  const resolveItemStatus = (blockType, item) => {
    if (item.kind === "style") return { status: "free" };
    if (item.kind === "material" || item.kind === "animation") {
      if (item.id === DEFAULT_MATERIAL_TEXTURE_ID) return { status: "free" };
      const ownership = resolveOwnership(item.kind === "material" ? BLOCK_TYPE_MATERIAL : BLOCK_TYPE_ANIMATION, item.id);
      return ownership.unlocked ? { status: "won" } : { status: "shop", price: "5000" };
    }
    const ownership = resolveOwnership(blockType, item.id);
    if (!ownership.product) return { status: "free" };
    if (ownership.accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER) {
      return ownership.unlocked
        ? { status: "won" }
        : { status: "lock", requirement: `Top #1 ${getDesignModeName(language, ownership.requiredMode)}` };
    }
    return ownership.unlocked
      ? { status: "won" }
      : { status: "shop", price: ownership.product.price || "50" };
  };

  const blockCatalog = useMemo(() => {
    const blockType = blockEditing === "common" ? BLOCK_TYPE_COMMON : BLOCK_TYPE_SPECIAL;
    const groups = blockEditing === "common" ? COMMON_SKIN_GROUPS_WITH_COUNTS_VISIBLE : SPECIAL_SKIN_GROUPS_WITH_COUNTS;
    const items = [];
    BLOCK_STYLES.forEach((style) => {
      items.push({
        key: `style-${style.id}`,
        kind: "style",
        id: style.id,
        label: style.label,
        thumb: style.thumbByColor ? BLOCK_STYLE_STAR_THUMBS[teamColorId] || style.thumb : style.thumb,
        category: style.category,
        rarity: style.rarity,
        description: style.description,
      });
    });
    groups.forEach((group) => {
      group.items.forEach((item) => {
        if (group.key !== "flags" && item.id === DEFAULT_SPECIAL_BLOCK_ID && blockEditing === "common") return;
        items.push({
          key: `skin-${group.key}-${item.id}`,
          kind: "skin",
          id: item.id,
          label: getSkinItemLabel(language, item),
          thumb: item.asset,
          isImage: true,
          category: group.key === "flags" ? "banderas" : "clasicos",
          rarity: group.key === "paid" ? "RARO" : group.key === "earned" ? "ÉPICO" : "COMÚN",
          description:
            group.key === "flags"
              ? `Bandera de ${getSkinItemLabel(language, item)} en las caras del bloque.`
              : item.id === DEFAULT_SPECIAL_BLOCK_ID
              ? "La estrella negra de siempre."
              : "Imagen en las caras del bloque.",
          source: item,
        });
      });
    });
    if (blockEditing === "common") {
      MATERIAL_TEXTURE_OPTIONS.filter((option) => option.id !== DEFAULT_MATERIAL_TEXTURE_ID).forEach((option) => {
        items.push({
          key: `material-${option.id}`,
          kind: "material",
          id: option.id,
          label: option.label,
          thumb: option.image,
          isImage: true,
          category: /lava|grass|basalt/.test(option.id) ? "naturaleza" : "materiales",
          rarity: "RARO",
          description: "Textura de material para todos tus bloques comunes.",
          source: option,
        });
      });
      BLOCK_ANIMATION_OPTIONS.filter((option) => option.id !== DEFAULT_BLOCK_ANIMATION_ID).forEach((option) => {
        items.push({
          key: `animation-${option.id}`,
          kind: "animation",
          id: option.id,
          label: option.label,
          thumb: option.image,
          isImage: true,
          category: /neon|glow|energy|one-line|voxel|sci-fi/.test(option.id) ? "neon" : "materiales",
          rarity: "ÉPICO",
          description: "Bloque animado en 3D.",
          source: option,
        });
      });
    }
    return items.map((item) => ({ ...item, ...resolveItemStatus(blockType, item) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockEditing, language, profile, botDesignSlot, teamColorId]);

  const currentBlockKey = useMemo(() => {
    if (blockEditing === "special") {
      const style = getBlockStyle(specialBlockId);
      if (style) return `style-${style.id}`;
      const match = blockCatalog.find((item) => item.kind === "skin" && item.id === specialBlockId);
      return match?.key || "";
    }
    if (blockAnimationId !== DEFAULT_BLOCK_ANIMATION_ID) return `animation-${blockAnimationId}`;
    if (blockMaterialTextureId !== DEFAULT_MATERIAL_TEXTURE_ID) return `material-${blockMaterialTextureId}`;
    const style = getBlockStyle(commonBlockId);
    if (style) return `style-${style.id}`;
    if (commonBlockId === DEFAULT_COMMON_BLOCK_ID) return "style-xs-plain";
    const match = blockCatalog.find((item) => item.kind === "skin" && item.id === commonBlockId);
    return match?.key || "";
  }, [blockAnimationId, blockCatalog, blockEditing, blockMaterialTextureId, commonBlockId, specialBlockId]);

  const styleThumb = (style) => (style.thumbByColor ? BLOCK_STYLE_STAR_THUMBS[teamColorId] || style.thumb : style.thumb);
  const findSkinItem = (groups, id) => {
    for (const group of groups) {
      const item = group.items.find((entry) => entry.id === id);
      if (item) return item;
    }
    return null;
  };
  const describeCurrentBlock = (kind) => {
    if (kind === "special") {
      const style = getBlockStyle(specialBlockId);
      if (style) return { label: style.label, thumb: styleThumb(style) };
      const item = findSkinItem(SPECIAL_SKIN_GROUPS_WITH_COUNTS, specialBlockId);
      return { label: item ? getSkinItemLabel(language, item) : specialBlockId, image: specialAsset };
    }
    if (blockAnimationId !== DEFAULT_BLOCK_ANIMATION_ID) return { label: selectedBlockAnimation.label, image: selectedBlockAnimation.image };
    if (blockMaterialTextureId !== DEFAULT_MATERIAL_TEXTURE_ID) return { label: selectedBlockMaterial.label, image: selectedBlockMaterial.image };
    const style = getBlockStyle(commonBlockId);
    if (style) return { label: style.label, thumb: styleThumb(style) };
    if (commonBlockId === DEFAULT_COMMON_BLOCK_ID) return { label: "Liso", thumb: getBlockStyle("xs-plain").thumb };
    const item = findSkinItem(COMMON_SKIN_GROUPS_WITH_COUNTS, commonBlockId);
    return { label: item ? getSkinItemLabel(language, item) : commonBlockId, image: commonAsset };
  };

  const visibleBlocks = blockCatalog.filter(
    (item) =>
      (blockFilter === "all" || item.category === blockFilter) &&
      (blockStatusFilter === "all" || item.status === blockStatusFilter),
  );

  // El carrusel se coloca en el bloque en uso al cambiar de común/especial o de filtro.
  useEffect(() => {
    if (loading) return;
    const syncKey = `${blockEditing}|${blockFilter}|${blockStatusFilter}|${currentBlockKey}`;
    if (blockCarouselSyncKeyRef.current === syncKey) return;
    blockCarouselSyncKeyRef.current = syncKey;
    const inUse = visibleBlocks.findIndex((item) => item.key === currentBlockKey);
    setBlockCarouselIndex(inUse >= 0 ? inUse : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockEditing, blockFilter, blockStatusFilter, currentBlockKey, visibleBlocks.length, loading]);

  const focusedBlock = visibleBlocks[Math.min(blockCarouselIndex, Math.max(0, visibleBlocks.length - 1))] || null;

  // La vista previa enseña el bloque del centro del catálogo sobre tu tablero,
  // sin guardarlo; el tablero no cambia aquí.
  const blockPreviewOverrides = (() => {
    const item = focusedBlock;
    if (!item) return {};
    if (blockEditing === "special") {
      if (item.kind === "style") return { specialStyle: item.id };
      if (item.kind === "skin") return { specialStyle: "", specialAsset: item.thumb, specialBlockId: item.id };
      return {};
    }
    const reset = {
      commonStyle: "",
      blockMaterial: MATERIAL_TEXTURE_OPTIONS[0],
      blockAnimation: BLOCK_ANIMATION_OPTIONS[0],
    };
    if (item.kind === "style") return { ...reset, commonStyle: item.id };
    if (item.kind === "skin") return { ...reset, commonAsset: item.thumb, commonBlockId: item.id };
    if (item.kind === "material") return { ...reset, blockMaterial: item.source };
    if (item.kind === "animation") return { ...reset, blockAnimation: item.source };
    return {};
  })();

  const applyBlockItem = (item) => {
    if (!item) return;
    const blockType = blockEditing === "common" ? BLOCK_TYPE_COMMON : BLOCK_TYPE_SPECIAL;
    if (item.status === "lock") {
      notification.warning({
        message: "Bloque bloqueado",
        description: `${item.label}: ${item.requirement || "aún no está disponible para ti"}.`,
      });
      return;
    }
    if (item.kind === "material") {
      handleBlockMaterialSelection(item.source);
      return;
    }
    if (item.kind === "animation") {
      handleAnimationSelection(item.source);
      return;
    }
    if (item.kind === "style") {
      if (blockEditing === "common") {
        setCommonBlockId(item.id === "xs-plain" ? DEFAULT_COMMON_BLOCK_ID : item.id);
        setBlockMaterialTextureId(DEFAULT_MATERIAL_TEXTURE_ID);
        setBlockAnimationId(DEFAULT_BLOCK_ANIMATION_ID);
      } else {
        setSpecialBlockId(item.id);
      }
      return;
    }
    handleSkinSelection(blockType, item.source || item, blockEditing === "common" ? setCommonBlockId : setSpecialBlockId);
  };

  const renderBlockThumb = (item, className = "") =>
    item?.thumb ? (
      <img className={`${className}${item.isImage ? " is-flat" : ""}`} src={item.thumb} alt="" draggable="false" />
    ) : item?.image ? (
      <img className={`${className} is-flat`} src={item.image} alt="" draggable="false" />
    ) : null;

  const CARD_TINTS = {
    clasicos: ["#3a2d10", "#0d0a03"],
    materiales: ["#2c3440", "#0a0d12"],
    naturaleza: ["#0d3a2a", "#020c08"],
    banderas: ["#14382a", "#04110a"],
    neon: ["#0d3a48", "#020b14"],
    galaxia: ["#2a1450", "#08051a"],
  };

  const blockCards = visibleBlocks.map((item) => {
    const tint = item.id === "xs-neon-pink" ? ["#3a0f3a", "#0c030f"] : item.id === "xs-lava" ? ["#4a1608", "#110402"] : CARD_TINTS[item.category] || CARD_TINTS.materiales;
    return {
      key: item.key,
      label: item.label,
      c1: tint[0],
      c2: tint[1],
      content: (
        <>
          {renderBlockThumb(item, "xima-studio-block-img")}
          <StudioBadge
            status={item.key === currentBlockKey ? "on" : item.status}
            price={item.price}
            requirement={item.requirement}
            className="xima-studio-card-badge"
          />
          <div className="xima-studio-card-name is-centered">{item.label}</div>
        </>
      ),
    };
  });

  const renderSlotCard = (kind) => {
    const current = describeCurrentBlock(kind);
    const editing = blockEditing === kind;
    return (
      <button
        type="button"
        className={`xima-studio-panel xima-studio-slot${editing ? " is-on" : ""}`}
        onClick={() => setBlockEditing(kind)}
        aria-pressed={editing}
      >
        {editing ? <span className="xima-studio-slot-edit">EDITANDO</span> : null}
        {renderBlockThumb(current, "xima-studio-slot-img")}
        <span className="xima-studio-slot-text">
          <span className="xima-studio-slot-k">{editing ? "PASO 1 · ELIGE QUÉ CAMBIAR" : "TOCA PARA EDITAR"}</span>
          <span className="xima-studio-slot-t">{kind === "common" ? "Bloque común" : "Bloque especial"}</span>
          <span className="xima-studio-slot-d">
            {kind === "common" ? "Los bloques normales que mueves en cada turno." : "Tu bloque único. El que debe llegar a la cima."}
          </span>
          <span className="xima-studio-slot-now">
            Ahora: <b className={kind === "common" ? "is-cyan" : "is-gold"}>{current.label}</b>
          </span>
        </span>
      </button>
    );
  };

  const windowStart = Math.max(0, Math.min(blockCarouselIndex - 2, visibleBlocks.length - 5));
  const windowSize = Math.min(5, visibleBlocks.length);

  const renderBotAdminRow = () =>
    isAdminViewer ? (
      <article className="xima-studio-panel xima-studio-bots">
        <span className="xima-studio-h">Bots (solo admins)</span>
        <div className="xima-design-bot-slots" role="group" aria-label="Diseño de bloques de los bots">
          {[1, 2, 3, 4].map((slot) => {
            const slotColorId = String(botDesigns[slot]?.blockColorId || "");
            return (
              <div key={slot} className="xima-design-bot-slot">
                <button
                  type="button"
                  className={botDesignSlot === slot ? "is-selected" : ""}
                  onClick={() => selectBotDesignSlot(slot)}
                  aria-pressed={botDesignSlot === slot}
                  disabled={saving || loading}
                >
                  Bot {slot}
                </button>
                <div className="xima-design-bot-colors" role="group" aria-label={`Color de bloque del Bot ${slot}`}>
                  {BOT_BLOCK_COLOR_OPTIONS.map((option) => {
                    const checked = slotColorId === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        className={`xima-design-bot-color${checked ? " is-checked" : ""}`}
                        style={{ background: option.hex }}
                        onClick={() => toggleBotBlockColor(slot, option.id)}
                        aria-pressed={checked}
                        title={`${option.label}${checked ? " (activo)" : ""}`}
                        aria-label={`Bot ${slot}: bloque ${option.label}`}
                        disabled={botColorSavingSlot === slot}
                      >
                        {checked ? <IoCheckmarkCircleOutline aria-hidden="true" /> : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        {botDesignSlot ? (
          <p className="xima-design-bot-slot-note">
            Editando los bloques del Bot {botDesignSlot}. Pulsa Bot {botDesignSlot} otra vez para volver a tu diseño.
          </p>
        ) : null}
      </article>
    ) : null;

  const renderBlocksTab = () => (
    <section className="xima-studio-blocks">
      <div className="xima-studio-blocks-row">
        {renderSlotCard("common")}
        {renderSlotCard("special")}
        <article className="xima-studio-panel xima-studio-mini">
          <div className="xima-studio-mini-head">
            <div>
              <h2 className="xima-studio-h">Vista previa en vivo</h2>
              <p className="xima-studio-sub">Tus bloques sobre tu tablero · arrastra para girar</p>
            </div>
            <div className="xima-studio-team" role="group" aria-label="Color de tu equipo">
              {TEAM_COLORS.map((color) => (
                <button
                  key={color.id}
                  type="button"
                  className={teamColorId === color.id ? "is-on" : ""}
                  style={{ background: color.hex }}
                  onClick={() => setTeamColorId(color.id)}
                  aria-pressed={teamColorId === color.id}
                  aria-label={`Equipo ${color.id}`}
                  title="Color de tu equipo"
                />
              ))}
            </div>
          </div>
          <div className="xima-studio-mini-view">{renderBoardPreview(blockPreviewOverrides)}</div>
        </article>
      </div>

      <article className="xima-studio-panel xima-studio-catalog">
        <div className="xima-studio-row">
          <div>
            <h2 className="xima-studio-h">Paso 2 · Catálogo de bloques</h2>
            <p className="xima-studio-sub">Desliza los bloques como discos. El del centro es el que estás viendo.</p>
          </div>
          <span className="xima-studio-spacer" />
          <StudioChips options={BLOCK_FILTERS} value={blockFilter} onChange={setBlockFilter} />
          <span className="xima-studio-divider" />
          <StudioChips options={STATUS_FILTERS} value={blockStatusFilter} onChange={setBlockStatusFilter} small dots />
        </div>
        {blockCards.length ? (
          <CoverFlow
            items={blockCards}
            index={blockCarouselIndex}
            onIndexChange={setBlockCarouselIndex}
            cardWidth={290}
            cardHeight={290}
            gap={205}
            sideScale={0.78}
            className="is-blocks"
          />
        ) : (
          <p className="xima-studio-empty">No hay bloques con este filtro.</p>
        )}
      </article>

      <article className="xima-studio-panel xima-studio-detail">
        {focusedBlock ? (
          <>
            {renderBlockThumb(focusedBlock, "xima-studio-detail-img")}
            <div className="xima-studio-detail-text">
              <div className="xima-studio-detail-title">
                <b>{focusedBlock.label}</b>
                <span className="xima-studio-rarity">{focusedBlock.rarity}</span>
                <StudioBadge
                  status={focusedBlock.key === currentBlockKey ? "on" : focusedBlock.status}
                  price={focusedBlock.price}
                  requirement={focusedBlock.requirement}
                />
              </div>
              <p>{focusedBlock.description}</p>
            </div>
            <span className="xima-studio-spacer" />
            <StudioLegend />
            <button type="button" className="xima-studio-btn" onClick={() => setCloseLookItem(focusedBlock)}>
              Ver de cerca
            </button>
            <button
              type="button"
              className="xima-studio-btn is-primary is-big"
              onClick={() => applyBlockItem(focusedBlock)}
              disabled={focusedBlock.key === currentBlockKey}
            >
              {focusedBlock.key === currentBlockKey
                ? "En uso"
                : focusedBlock.status === "shop"
                ? "Comprar"
                : blockEditing === "common"
                ? "Usar en bloque común"
                : "Usar en bloque especial"}
            </button>
          </>
        ) : null}
      </article>

      <article className="xima-studio-panel xima-studio-strip">
        <span className="xima-studio-strip-label">TODA LA COLECCIÓN · {blockCatalog.length}</span>
        <div className="xima-studio-strip-track">
          {visibleBlocks.map((item, index) => (
            <button
              key={item.key}
              type="button"
              className={`xima-studio-strip-item${index >= windowStart && index < windowStart + windowSize ? " is-visible" : ""}${index === blockCarouselIndex ? " is-focus" : ""}`}
              onClick={() => setBlockCarouselIndex(index)}
              title={item.label}
            >
              {renderBlockThumb(item)}
            </button>
          ))}
        </div>
      </article>

      {renderBotAdminRow()}

      <Modal
        open={Boolean(closeLookItem)}
        onCancel={() => setCloseLookItem(null)}
        footer={null}
        centered
        className="xima-studio-closelook"
        title={closeLookItem?.label}
      >
        {closeLookItem ? (
          <div className="xima-studio-closelook-body">
            {renderBlockThumb(closeLookItem, "xima-studio-closelook-img")}
            <p>{closeLookItem.description}</p>
          </div>
        ) : null}
      </Modal>
    </section>
  );

  // ---------------------------------------------------------------------
  // Fondo
  // ---------------------------------------------------------------------

  const renderBackgroundTab = () => (
    <section className="xima-studio-background">
      <article className="xima-studio-panel xima-studio-stage is-background">
        <div className="xima-studio-stage-head">
          <div>
            <h2 className="xima-studio-h">Vista previa en vivo</h2>
            <p className="xima-studio-sub">Así se ve el fondo detrás del tablero</p>
          </div>
        </div>
        <div className="xima-studio-stage-view">{renderBoardPreview({ autoRotate: true })}</div>
      </article>
      <article className="xima-studio-panel xima-studio-parts-panel">
        <h2 className="xima-studio-h">Fondo del área de juego</h2>
        <p className="xima-studio-sub">Elige lo que se ve detrás del tablero en tus partidas.</p>
        <div className="xima-studio-grid is-backgrounds">
          {GAME_BACKGROUND_OPTIONS.map((option) => {
            const selected = gameBackgroundId === option.id;
            return (
              <button
                key={option.id}
                type="button"
                className={`xima-studio-item is-background ${option.previewClassName || ""}${selected ? " is-selected" : ""}`}
                onClick={() => setGameBackgroundId(option.id)}
                aria-pressed={selected}
              >
                <span
                  className="xima-studio-swatch xima-studio-item-image"
                  style={
                    option.image
                      ? { backgroundImage: `url("${option.image}")` }
                      : { background: option.plainColor || "radial-gradient(circle at 30% 30%, #2b3f8f, #060a17 70%)" }
                  }
                />
                <StudioBadge status={selected ? "on" : "free"} className="xima-studio-item-badge" />
                <span className="xima-studio-item-name">{option.label}</span>
              </button>
            );
          })}
        </div>
      </article>
    </section>
  );

  const STUDIO_TABS = [
    { id: "board", icon: "board", label: "Tablero" },
    { id: "blocks", icon: "cube", label: "Bloques" },
    { id: "background", icon: "bg", label: "Fondo" },
    { id: "profile", icon: "user", label: "Perfil" },
  ];

  const walletBalance = Number(profile?.walletBalance ?? profile?.wallet?.balance ?? 0) || 0;

  return (
    <main className="xima-studio">
      <header className="xima-studio-top">
        <div className="xima-studio-logo">XIMA</div>
        <div className="xima-studio-title">
          <b>{designText("designMode")}</b>
          <span>Personaliza tu tablero y tus bloques</span>
        </div>
        <nav className="xima-studio-tabs" aria-label="Secciones de modo diseñar">
          {STUDIO_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`xima-studio-tab${designWorkspace === tab.id ? " is-on" : ""}`}
              onClick={() => {
                if (tab.id === "profile" && botDesignSlot) selectBotDesignSlot(botDesignSlot);
                setDesignWorkspace(tab.id);
              }}
              aria-pressed={designWorkspace === tab.id}
            >
              <StudioIcon name={tab.icon} size={20} />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
        <span className="xima-studio-spacer" />
        <p className={`xima-studio-pill xima-studio-autosave is-${autoSaveState}`} role="status" aria-live="polite">
          <i aria-hidden="true" />
          {autoSaveState === "saving"
            ? designText("saving")
            : autoSaveState === "error"
            ? "No se pudo guardar"
            : botDesignSlot
            ? `Guardado automático · Bot ${botDesignSlot}`
            : "Guardado automático"}
        </p>
        <p className="xima-studio-pill xima-studio-wallet" title="Tu saldo">
          <StudioIcon name="gem" size={14} /> {walletBalance.toLocaleString("es-ES")}
        </p>
        <button type="button" className="xima-studio-btn xima-studio-lobby" onClick={() => navigate("/game/lobby")}>
          <StudioIcon name="back" size={16} /> {designText("backToLobby")}
        </button>
      </header>

      <div className="xima-studio-body">
        {designWorkspace === "board"
          ? renderBoardTab()
          : designWorkspace === "blocks"
          ? renderBlocksTab()
          : designWorkspace === "background"
          ? renderBackgroundTab()
          : (
            <div className="xima-profile-design-screen xima-studio-profile">
              <div className="xima-profile-design-layout">{renderProfileWorkspace()}</div>
            </div>
          )}
      </div>
    </main>
  );
};

export default DesignMode;
